/**
 * Custom Local Delivery - Cart orchestrator.
 * Fetches config from planners.dand3.com, validates postal, populates timeslots.
 */
(function () {
  'use strict';

  function isFieldAssociatedWithForm(field, form) {
    if (!field || !form) return false;
    if (field.form && field.form === form) return true;
    var formId = typeof form.getAttribute === 'function' ? form.getAttribute('id') : form.id;
    if (formId && typeof field.getAttribute === 'function' && field.getAttribute('form') === formId) return true;
    return !!(typeof form.contains === 'function' && form.contains(field));
  }

  function isKeyboardSubmitProneField(field, form) {
    if (!field || !form || !isFieldAssociatedWithForm(field, form)) return false;
    var tagName = String(field.tagName || '').toLowerCase();
    if (tagName !== 'input') return false;
    var type = String(field.type || 'text').toLowerCase();
    return !(
      type === 'hidden' ||
      type === 'button' ||
      type === 'submit' ||
      type === 'reset' ||
      type === 'checkbox' ||
      type === 'radio' ||
      type === 'file' ||
      type === 'image'
    );
  }

  function applyNonSubmittingEnterKeyHints(scope) {
    if (!scope || typeof scope.querySelectorAll !== 'function') return;
    var fields = scope.querySelectorAll(
      'input[type="text"], input[type="email"], input[type="tel"], input[type="search"], input[type="url"], input[type="number"], input[type="date"]'
    );
    for (var i = 0; i < fields.length; i++) {
      if (!fields[i] || typeof fields[i].setAttribute !== 'function') continue;
      if (!fields[i].getAttribute || !fields[i].getAttribute('enterkeyhint')) {
        fields[i].setAttribute('enterkeyhint', 'next');
      }
    }
  }

  function bindCartFormKeyboardSubmitGuard(form, scope) {
    if (!form || form.__wfLdKeyboardSubmitGuardBound) return;
    form.__wfLdKeyboardSubmitGuardBound = true;

    var keydownScope = scope && typeof scope.addEventListener === 'function' ? scope : form;
    applyNonSubmittingEnterKeyHints(keydownScope);

    keydownScope.addEventListener('keydown', function (event) {
      if (!event || event.key !== 'Enter') return;
      if (!isKeyboardSubmitProneField(event.target, form)) return;
      event.preventDefault();
    });

    form.addEventListener('submit', function (event) {
      if (!event) return;
      if (event.submitter) return;
      if (!isKeyboardSubmitProneField(document.activeElement, form)) return;
      event.preventDefault();
    });
  }

  // NOTE: the anonymous-sender liner in the order note is enforced SERVER-SIDE in the
  // calendar-worker (orders/create → prependAnonymousSenderNote) keyed off the
  // `sender_anonymous` attribute this form still sets in updateAttributes. A theme-side
  // checkout-time hook proved unreliable on Eurus's native checkout, so it was removed
  // in favour of the worker path (works across all stores + checkout paths). See
  // docs/Cart-Form.md.

  function normalizeDeliveryPostal(value) {
    return String(value || '').trim();
  }

  function getDeliveryPostalInput() {
    return document.getElementById('wf-ld-recipient-postal');
  }

  function getDeliveryPostalCodeFromInput(input) {
    return input ? normalizeDeliveryPostal(input.value) : '';
  }

  function isDeliveryAddressGateReady(code, opts) {
    opts = opts || {};
    return String(code || '').trim().length === 6 && !!opts.streetFilled && !!opts.postalValid;
  }

  function normalizeSlotMinute(value) {
    if (value == null || value === '') return 0;
    var parsed = Number(value);
    return isFinite(parsed) ? parsed : 0;
  }

  function slotsShareTimeWindow(a, b) {
    if (!a || !b) return false;
    return Number(a.startHour) === Number(b.startHour) &&
      normalizeSlotMinute(a.startMinute) === normalizeSlotMinute(b.startMinute) &&
      Number(a.endHour) === Number(b.endHour) &&
      normalizeSlotMinute(a.endMinute) === normalizeSlotMinute(b.endMinute);
  }

  function slotsShareAvailableDay(a, b) {
    var aDays = a && Array.isArray(a.availableDays) ? a.availableDays : [];
    var bDays = b && Array.isArray(b.availableDays) ? b.availableDays : [];
    if (!aDays.length || !bDays.length) return true;
    return aDays.some(function (day) { return bDays.indexOf(day) >= 0; });
  }

  function addPricingCandidate(candidates, slotId) {
    var normalized = String(slotId || '').trim();
    if (!normalized || candidates.indexOf(normalized) >= 0) return;
    candidates.push(normalized);
  }

  function resolveMappedPricingTierId(pricing, slotId, slot, defaultSlots) {
    if (!pricing || !pricing.slotTierMap) return '';
    var candidates = [];
    addPricingCandidate(candidates, slotId);
    addPricingCandidate(candidates, slot && slot.pricingSlotId);
    addPricingCandidate(candidates, slot && slot.sourceSlotId);

    var dayMatches = [];
    var timeMatches = [];
    (defaultSlots || []).forEach(function (defaultSlot) {
      if (!slotsShareTimeWindow(slot, defaultSlot)) return;
      if (slotsShareAvailableDay(slot, defaultSlot)) dayMatches.push(defaultSlot);
      else timeMatches.push(defaultSlot);
    });

    dayMatches.concat(timeMatches).forEach(function (defaultSlot) {
      addPricingCandidate(candidates, defaultSlot && defaultSlot.id);
    });

    for (var i = 0; i < candidates.length; i++) {
      var mappedTierId = pricing.slotTierMap[candidates[i]];
      if (mappedTierId) return mappedTierId;
    }
    return '';
  }

  function createInitController(ctx) {
    ctx = ctx || {};
    var deps = ctx.deps || {};

    var API = deps.API || window.WF_LD_API;
    var DATE = deps.DATE || window.WF_LD_DATE_UTILS;
    var POSTAL = deps.POSTAL || window.WF_LD_POSTAL_UTILS;
    var SLOT = deps.SLOT || window.WF_LD_SLOT_UTILS;
    var STORAGE = deps.STORAGE || window.WF_LD_STORAGE;
    var EXPRESS = deps.EXPRESS || window.WF_LD_EXPRESS_CART;
    var PICKER = deps.PICKER || window.WF_LD_DATE_PICKER;
    var ADDRESS_AC = deps.ADDRESS_AC || window.WF_LD_ADDRESS_AC;
    var DOMAIN = deps.DOMAIN || window.WF_LD_CART_DOMAIN;
    var TIMESLOTS = deps.TIMESLOTS || window.WF_LD_CART_TIMESLOTS;
    var CHECKOUT = deps.CHECKOUT || window.WF_LD_CART_CHECKOUT;
    var VALIDATION = deps.VALIDATION || window.WF_LD_CART_VALIDATION;
    var CHECKOUT_FLOW = deps.CHECKOUT_FLOW || window.WF_LD_CART_CHECKOUT_FLOW;
    var UI = deps.UI || window.WF_LD_CART_UI;
    var SAVED = deps.SAVED || window.WF_LD_SAVED_ADDRESSES;

    if (!API || !DATE || !POSTAL || !SLOT || !STORAGE || !EXPRESS || !DOMAIN || !TIMESLOTS || !CHECKOUT || !VALIDATION || !CHECKOUT_FLOW || !UI) {
      console.warn('[wf-ld-init] Missing modules');
      return {
        init: function () {},
        startMixedCartObservers: function () {},
        disposeMixedCartObservers: function () {},
      };
    }

  var config = null;
  var activeTimeslotType = 'standard'; // 'standard' | 'express'
  var seasonalData = null; // { variantWindows: Record<string, [{startDate,endDate,name?,productTitle?}]> }
  var seasonalDataPromise = null;
  var seasonalCheckPending = false;
  var seasonalConflictMessages = [];
  var datePicker = null;
  var dateSelected = false;
  var matchedOccasion = null; // set when cart has an occasion product
  var cartProductIds = [];
  var cartVariantIds = [];
  var advanceBookingRulesResolved = [];
  var activeAdvanceLeadDays = 0;
  var mixedCartObserver = null;
  var mixedCartVisibilityHandler = null;
  var expressSelectionError = '';
  var bootstrapInitGeneration = 0;
  var formInitGeneration = 0;
  var timeslotController = null;
  var validationController = null;
  var checkoutFlowController = null;
  var uiController = null;
  // Controller-scoped handle to the current form session's disposer. The
  // per-node disposer stored on wrap.__wfLdV2Dispose is lost when a silent cart
  // refresh replaces #wf-ld-custom with a freshly-rendered node, so we also keep
  // it here to guarantee the prior session (and its now-detached UI controller)
  // is torn down before re-init. See disposeFormSession use in initForm.
  var activeFormDispose = null;
  var savedAddressController = null;
  var recipientAutocompleteApplying = false;
  var addressConsistencyTimer = null;
  var bootstrapRecoveryMessage = '';
  var cartFormTrackingState = {
    started: false,
    identifiedEmail: '',
  };

  function getEl(id) { return document.getElementById(id); }

  function getCurrentTrackingDeliveryMethod() {
    var methodDelivery = getEl('wf-ld-method-delivery');
    if (activeTimeslotType === 'express') return 'Express';
    return (methodDelivery && methodDelivery.checked) ? 'Delivery' : 'Collection';
  }

  function buildCartFormTrackingMeta() {
    return {
      deliveryMethod: getCurrentTrackingDeliveryMethod(),
      senderEmail: getFormVal('sender_email'),
    };
  }

  function trackCartFormProgress() {
    if (!API || typeof API.reportCartFormInteraction !== 'function') return;
    var hasSignal = Boolean(
      getFormVal('recipient_first_name') ||
      getFormVal('recipient_last_name') ||
      getFormVal('recipient_address1') ||
      getFormVal('recipient_postal') ||
      getFormVal('sender_first_name') ||
      getFormVal('sender_last_name') ||
      getFormVal('sender_email') ||
      getFormVal('sender_phone') ||
      getSelectedDateIso() ||
      getActiveTimeslotValue()
    );
    if (hasSignal && !cartFormTrackingState.started) {
      cartFormTrackingState.started = true;
      API.reportCartFormInteraction('form_started', buildCartFormTrackingMeta());
    }

    var senderEmail = String(getFormVal('sender_email') || '').trim().toLowerCase();
    if (senderEmail && cartFormTrackingState.identifiedEmail !== senderEmail) {
      cartFormTrackingState.identifiedEmail = senderEmail;
      API.reportCartFormInteraction('identified', buildCartFormTrackingMeta());
    }
  }

  function normalizeShopifyNumericId(value) {
    return DOMAIN.normalizeShopifyNumericId(value);
  }

  function uniqueCartProductIds(cart) {
    return DOMAIN.uniqueCartProductIds(cart);
  }

  function uniqueCartVariantIds(cart) {
    var items = (cart && cart.items) || [];
    var seen = {};
    var ids = [];
    for (var i = 0; i < items.length; i++) {
      var normalized = normalizeShopifyNumericId(items[i] && items[i].variant_id);
      if (!normalized || seen[normalized]) continue;
      seen[normalized] = true;
      ids.push(normalized);
    }
    return ids;
  }

  function getSeasonalWindowsForVariantId(variantId) {
    if (!seasonalData) return null;
    var normalized = normalizeShopifyNumericId(variantId);
    if (!normalized) return null;
    if (seasonalData[normalized]) return seasonalData[normalized];

    var prefixedNumeric = 'shopify-variant-' + normalized;
    if (seasonalData[prefixedNumeric]) return seasonalData[prefixedNumeric];

    var prefixedGid = 'shopify-variant-gid://shopify/ProductVariant/' + normalized;
    if (seasonalData[prefixedGid]) return seasonalData[prefixedGid];

    var gid = 'gid://shopify/ProductVariant/' + normalized;
    if (seasonalData[gid]) return seasonalData[gid];

    var keys = Object.keys(seasonalData);
    for (var i = 0; i < keys.length; i++) {
      if (normalizeShopifyNumericId(keys[i]) === normalized) return seasonalData[keys[i]];
    }
    return null;
  }

  function isDateAllowedForSeasonalCart(dateIso) {
    if (!dateIso || !seasonalData || !cartVariantIds.length) return true;
    for (var i = 0; i < cartVariantIds.length; i++) {
      var windows = getSeasonalWindowsForVariantId(cartVariantIds[i]);
      if (!windows || !windows.length) continue;
      var withinAny = windows.some(function (window) {
        return dateIso >= window.startDate && dateIso <= window.endDate;
      });
      if (!withinAny) return false;
    }
    return true;
  }

  function collectExpressTierVariantIdsFromConfig(cfg) {
    if (!cfg || !cfg.express_pricing || typeof cfg.express_pricing !== 'object') return [];
    var tiers = Array.isArray(cfg.express_pricing.tiers) ? cfg.express_pricing.tiers : [];
    var seen = {};
    var ids = [];
    tiers.forEach(function (tier) {
      if (!tier || tier.active === false) return;
      var id = String(tier.variantId || '').trim();
      if (!id || seen[id]) return;
      seen[id] = true;
      ids.push(id);
    });
    return ids;
  }

  function formatExpressAmount(amount) {
    if (amount == null || !isFinite(amount)) return null;
    var rounded = Math.round(Number(amount) * 100) / 100;
    if (Math.abs(rounded - Math.round(rounded)) < 0.0001) return String(Math.round(rounded));
    return rounded.toFixed(2).replace(/\.00$/, '');
  }

  function getExpressPricingConfig() {
    if (!config || !config.express_pricing || typeof config.express_pricing !== 'object') return null;
    var tiers = Array.isArray(config.express_pricing.tiers) ? config.express_pricing.tiers : [];
    var slotTierMap = config.express_pricing.slotTierMap && typeof config.express_pricing.slotTierMap === 'object'
      ? config.express_pricing.slotTierMap
      : {};
    var defaultTierId = config.express_pricing.defaultTierId || '';
    return { tiers: tiers, slotTierMap: slotTierMap, defaultTierId: defaultTierId };
  }

  function getDeliveryPricingConfig() {
    if (!config || !config.delivery_pricing || typeof config.delivery_pricing !== 'object') return null;
    var tiers = Array.isArray(config.delivery_pricing.tiers) ? config.delivery_pricing.tiers : [];
    var slotTierMap = config.delivery_pricing.slotTierMap && typeof config.delivery_pricing.slotTierMap === 'object'
      ? config.delivery_pricing.slotTierMap
      : {};
    var defaultTierId = config.delivery_pricing.defaultTierId || '';
    return { tiers: tiers, slotTierMap: slotTierMap, defaultTierId: defaultTierId };
  }

  function getExpressSlotPricingMeta(slotId, slot) {
    var pricing = getExpressPricingConfig();
    if (!pricing) {
      return {
        tierId: '',
        variantId: '',
        amount: null,
      };
    }

    var tierId = resolveMappedPricingTierId(
      pricing,
      slotId,
      slot,
      config && Array.isArray(config.express_timeslots) ? config.express_timeslots : []
    );
    var tier = null;
    if (tierId) {
      tier = pricing.tiers.find(function (t) {
        return t && t.id === tierId && t.active !== false;
      }) || null;
    }
    var variantId = tier && tier.variantId ? String(tier.variantId) : '';
    var amount = tier && isFinite(Number(tier.amount)) ? Number(tier.amount) : null;
    return {
      tierId: tier ? tier.id : '',
      variantId: variantId || '',
      amount: amount,
    };
  }

  function getSelectedExpressSlotMeta() {
    var expSelect = getEl('wf-ld-express-timeslot');
    if (!expSelect || expSelect.selectedIndex < 0) return null;
    var opt = expSelect.options[expSelect.selectedIndex];
    if (!opt || !opt.value) return null;
    var amountAttr = opt.getAttribute('data-amount');
    var parsedAmount = amountAttr != null && amountAttr !== '' ? Number(amountAttr) : null;
    if (parsedAmount != null && !isFinite(parsedAmount)) parsedAmount = null;
    return {
      slotId: opt.getAttribute('data-slot-id') || '',
      tierId: opt.getAttribute('data-tier-id') || '',
      rawLabel: opt.getAttribute('data-raw-label') || opt.value || '',
      amount: parsedAmount,
      variantId: opt.getAttribute('data-variant-id') || '',
    };
  }

  function getSelectedStandardSlotMeta() {
    var stdSelect = getEl('wf-ld-timeslot');
    if (!stdSelect || stdSelect.selectedIndex < 0) return null;
    var opt = stdSelect.options[stdSelect.selectedIndex];
    if (!opt || !opt.value) return null;
    var amountAttr = opt.getAttribute('data-amount');
    var parsedAmount = amountAttr != null && amountAttr !== '' ? Number(amountAttr) : null;
    if (parsedAmount != null && !isFinite(parsedAmount)) parsedAmount = null;
    return {
      slotId: opt.getAttribute('data-slot-id') || '',
      tierId: opt.getAttribute('data-tier-id') || '',
      rawLabel: opt.getAttribute('data-raw-label') || opt.value || '',
      amount: parsedAmount,
      variantId: opt.getAttribute('data-variant-id') || '',
      feeLabel: opt.getAttribute('data-fee-label') || 'Delivery Fee',
    };
  }

  function getStandardSlotPricingMeta(slotId, slot) {
    var pricing = getDeliveryPricingConfig();
    if (!pricing) return { tierId: '', variantId: '', amount: null, hasTierRule: false };
    var tierId = resolveMappedPricingTierId(
      pricing,
      slotId,
      slot,
      config && Array.isArray(config.timeslots) ? config.timeslots : []
    );
    if (!tierId) return { tierId: '', variantId: '', amount: null, hasTierRule: false };
    var tier = pricing.tiers.find(function (t) {
      return t && t.id === tierId && t.active !== false;
    }) || null;
    var variantId = tier && tier.variantId ? String(tier.variantId) : '';
    var amount = tier && isFinite(Number(tier.amount)) ? Number(tier.amount) : null;
    return {
      tierId: tier ? tier.id : tierId,
      variantId: variantId,
      amount: amount,
      hasTierRule: true,
      feeLabel: tier && tier.label ? String(tier.label) : 'Delivery Fee',
    };
  }

  function getActiveTimeslotMeta() {
    if (activeTimeslotType === 'express') return getSelectedExpressSlotMeta();
    return getSelectedStandardSlotMeta();
  }

  function setDeliveryFeeRow(label, amount) {
    if (EXPRESS && typeof EXPRESS.setFeeCharge === 'function') {
      EXPRESS.setFeeCharge(label || 'Delivery Fee', amount);
      return;
    }
    var row = getEl('wf-express-charge-row');
    if (!row) return;
    var labelEl = getEl('wf-delivery-fee-label');
    if (labelEl) labelEl.textContent = label || 'Delivery Fee';
    var moneyEl = row.querySelector('.money');
    if (!moneyEl) return;
    if (amount == null || !isFinite(amount)) {
      moneyEl.textContent = '$0.00';
      return;
    }
    moneyEl.textContent = '$' + Number(amount).toFixed(2);
  }

  function updateExpressCardLabel() {
    var labelEl = getEl('wf-ld-express-label');
    if (!labelEl) return;
    if (activeTimeslotType !== 'express') {
      labelEl.textContent = 'Express';
      return;
    }
    var meta = getSelectedExpressSlotMeta();
    var amountLabel = meta && meta.amount != null ? formatExpressAmount(meta.amount) : null;
    labelEl.textContent = amountLabel ? ('Express (+$' + amountLabel + ')') : 'Express';
  }

  function updateStandardCardLabel() {
    var labelEl = getEl('wf-ld-standard-label');
    if (!labelEl) return;
    if (activeTimeslotType !== 'standard') {
      labelEl.textContent = 'Standard';
      return;
    }
    var meta = getSelectedStandardSlotMeta();
    var amountLabel = meta && meta.amount != null ? formatExpressAmount(meta.amount) : null;
    labelEl.textContent = amountLabel ? ('Standard (+$' + amountLabel + ')') : 'Standard';
  }

  function getCartAttributeField(attrName) {
    var name = 'attributes[' + attrName + ']';
    var form = (document.getElementById('cart') || document.getElementById('cart_form'));
    if (form) {
      var hiddenInForm = form.querySelector('input[type="hidden"][name="' + name + '"]');
      if (hiddenInForm) return hiddenInForm;
      var inForm = form.querySelector('[name="' + name + '"]');
      if (inForm) return inForm;
    }
    if (typeof document.querySelector !== 'function') return null;
    var hiddenDetached = document.querySelector('[form="cart"][type="hidden"][name="' + name + '"]');
    if (hiddenDetached) return hiddenDetached;
    return document.querySelector('[form="cart"][name="' + name + '"]');
  }

  function getFormVal(attrName) {
    var inp = getCartAttributeField(attrName);
    return inp ? inp.value : '';
  }

  function getInputValById(id) {
    var el = getEl(id);
    return el && typeof el.value === 'string' ? el.value.trim() : '';
  }

  function applyCollectionRecipientFallback(recipient, sender) {
    if (!recipient) recipient = {};
    if (!sender) sender = {};

    var collectionName = getInputValById('wf-ld-collection-name');
    var collectionAddress1 = getInputValById('wf-ld-collection-address1');
    var collectionAddress2 = getInputValById('wf-ld-collection-address2');
    var collectionPostal = getInputValById('wf-ld-collection-postal');
    var collectionPhone = getInputValById('wf-ld-collection-phone');

    if (!recipient.firstName) recipient.firstName = collectionName || sender.firstName || 'Self';
    if (!recipient.lastName) recipient.lastName = sender.lastName || 'Collection';
    if (!recipient.phone) recipient.phone = collectionPhone || sender.phone || '';
    if (!recipient.address1) recipient.address1 = collectionAddress1 || '';
    if (!recipient.address2) recipient.address2 = collectionAddress2 || '';
    if (!recipient.postal) recipient.postal = collectionPostal || '';

    return recipient;
  }

  function getPhoneWithCode(inputId, codeId) {
    var code = getEl(codeId);
    var inp = getEl(inputId);
    var prefix = code ? code.value : '+65';
    var raw = inp ? inp.value.trim() : '';
    var num = raw.replace(/\D/g, '');
    if (!num) return '';
    if (raw.charAt(0) === '+') return '+' + num;
    return prefix + num;
  }

  function setPhoneWithCode(inputId, codeId, fullVal) {
    var code = getEl(codeId);
    var inp = getEl(inputId);
    if (fullVal == null) return;
    var raw = String(fullVal).trim();
    if (!raw) {
      if (inp) inp.value = '';
      return;
    }
    if (raw.charAt(0) === '+' && code) {
      var prefix = '';
      var num = '';
      var opts = code.options;
      for (var i = 0; i < opts.length; i++) {
        var optionValue = opts[i].value || '';
        if (optionValue && raw.indexOf(optionValue) === 0 && optionValue.length > prefix.length) {
          prefix = optionValue;
          code.selectedIndex = i;
        }
      }
      if (prefix) {
        num = raw.slice(prefix.length).replace(/\D/g, '');
        if (inp) inp.value = num;
        return;
      }
    }
    if (inp) inp.value = raw;
  }

  function syncPhoneHidden() {
    var rFull = getEl('wf-ld-recipient-phone-full');
    if (rFull) rFull.value = getPhoneWithCode('wf-ld-recipient-phone', 'wf-ld-recipient-phone-code');
    var sFull = getEl('wf-ld-sender-phone-full');
    if (sFull) sFull.value = getPhoneWithCode('wf-ld-sender-phone', 'wf-ld-sender-phone-code');
  }

  function getActiveTimeslotValue() {
    var slotMeta = getActiveTimeslotMeta();
    return slotMeta ? (slotMeta.rawLabel || '') : '';
  }

  function getActiveTimeslotId() {
    var slotMeta = getActiveTimeslotMeta();
    return slotMeta ? (slotMeta.slotId || '') : '';
  }

  function getSelectedDateIso() {
    var dateEl = getEl('wf-ld-date');
    if (!dateEl) return '';
    var fromData = dateEl.getAttribute('data-value') || '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(fromData)) return fromData;
    var fromValue = dateEl.value || '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(fromValue)) return fromValue;
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(fromValue)) {
      var dmy = fromValue.split('/');
      return dmy[2] + '-' + dmy[1] + '-' + dmy[0];
    }
    return '';
  }

  function getDateBounds() {
    return DOMAIN.getDateBounds({
      matchedOccasion: matchedOccasion,
      activeAdvanceLeadDays: activeAdvanceLeadDays,
      config: config,
      DATE: DATE,
    });
  }

  function isDateWithinBounds(dateIso) {
    return DOMAIN.isDateWithinBounds(dateIso, getDateBounds());
  }

  function isDateAllowed(dateIso) {
    return DOMAIN.isDateAllowed(dateIso, getDateBounds(), isBlackoutDate) && isDateAllowedForSeasonalCart(dateIso);
  }

  function isSelectedTimeslotValid() {
    var slotVal = getActiveTimeslotValue();
    if (!slotVal) return false;
    var selectEl = activeTimeslotType === 'express'
      ? getEl('wf-ld-express-timeslot')
      : getEl('wf-ld-timeslot');
    if (!selectEl) return false;
    for (var i = 0; i < selectEl.options.length; i++) {
      var opt = selectEl.options[i];
      if (opt.value === slotVal) return !opt.disabled;
    }
    return false;
  }

  function getResolvedSlotsForActiveMode(dateIso) {
    if (!config || !dateIso) return [];
    var isCollection = !!(getEl('wf-ld-method-collection') && getEl('wf-ld-method-collection').checked);
    var mode = isCollection ? 'collection' : (activeTimeslotType === 'express' ? 'express' : 'delivery');
    var baseSlots = mode === 'collection'
      ? (config.collection_timeslots || [])
      : (mode === 'express' ? (config.express_timeslots || []) : (config.timeslots || []));
    return SLOT.resolveSlotsForDate(baseSlots, dateIso, config.override_days || [], mode);
  }

  function getActiveSlotConfig(dateIso) {
    var iso = dateIso || getSelectedDateIso();
    if (!iso) return null;
    var slotId = getActiveTimeslotId();
    var slotLabel = getActiveTimeslotValue();
    var slots = getResolvedSlotsForActiveMode(iso);
    if (!slots || slots.length === 0) return null;

    if (slotId) {
      for (var i = 0; i < slots.length; i++) {
        if (String(slots[i].id || '') === String(slotId)) return slots[i];
      }
    }
    if (slotLabel) {
      var normalized = String(slotLabel).trim();
      for (var j = 0; j < slots.length; j++) {
        if (String(deriveSlotLabel(slots[j])).trim() === normalized) return slots[j];
      }
    }
    return null;
  }

  function isActiveSlotPastCutoff(dateIso) {
    var iso = dateIso || getSelectedDateIso();
    var slot = getActiveSlotConfig(iso);
    if (!iso || !slot) return false;
    return !!DATE.isPastCutoff(slot, iso);
  }

  function updateAttributes() {
    var form = (document.getElementById('cart') || document.getElementById('cart_form'));
    if (!form) return;
    var method = (getEl('wf-ld-method-delivery') && getEl('wf-ld-method-delivery').checked) ? 'Delivery' : 'Collection';
    var dateIso = getSelectedDateIso();
    var slotMeta = getActiveTimeslotMeta();
    var slotLabel = slotMeta ? (slotMeta.rawLabel || '') : '';
    var slotId = slotMeta ? (slotMeta.slotId || '') : '';
    var isExpress = activeTimeslotType === 'express';
    var postal = getDeliveryPostalCodeFromInput(getDeliveryPostalInput());
    var timeVal = dateIso && slotLabel ? dateIso + ' ' + slotLabel : '';

    function setAttr(name, val) {
      var inp = getCartAttributeField(name);
      if (inp) inp.value = val;
    }
    function getAttr(name) {
      return getFormVal(name);
    }
    setAttr('Delivery Method', isExpress ? 'Express' : method);
    setAttr('Local Delivery Date and Time', timeVal); // always set for downstream (notifications, messageCard, email)
    if (method === 'Collection') {
      setAttr('Collection Date and Time', timeVal); // also set for display (avoids "Delivery" wording in Admin)
    } else {
      setAttr('Collection Date and Time', '');
    }
    setAttr('Delivery Area', method === 'Collection' ? '' : (postal || ''));
    setAttr('delivery_date', method === 'Collection' ? '' : (dateIso || ''));
    setAttr('delivery_timeslot', method === 'Collection' ? '' : (slotLabel || ''));
    setAttr('collection_date', method === 'Collection' ? (dateIso || '') : '');
    setAttr('collection_timeslot', method === 'Collection' ? (slotLabel || '') : '');
    setAttr('delivery_slot_id', slotId || '');

    // Anonymous sender
    var anonCb = getEl('wf-ld-anonymous');
    setAttr('sender_anonymous', (anonCb && anonCb.checked) ? 'Yes' : '');

    syncPhoneHidden();
  }

  var postalServerValid = null; // null=pending, true/false=result

  var postalIsHospital = false;
  var postalValidationRequest = 0;
  var postalBlockedNotice = null;
  var postalDefaultErrorMessage = '';

  function isDeliveryStreetFilled() {
    var streetInput = getEl('wf-ld-recipient-address1');
    return !!(streetInput && String(streetInput.value || '').trim());
  }

  function resolveDeliveryPostalValid(code) {
    if (code.length !== 6) return { valid: false, hospital: false };
    if (POSTAL.isHospitalPostal(code)) return { valid: true, hospital: true };
    if (postalServerValid !== null) return { valid: postalServerValid, hospital: false };
    var excludedAreas = config && config.excluded_areas ? config.excluded_areas : [];
    var excludedPrefixes = config && config.excluded_postal_prefixes ? config.excluded_postal_prefixes : [];
    var allowedExceptions = config && config.allowed_postal_exceptions ? config.allowed_postal_exceptions : [];
    var r = POSTAL.validatePostalClient(code, excludedAreas, excludedPrefixes, allowedExceptions, '', config && config.scheduled_postal_exclusions);
    return { valid: !!(r.valid && !r.excluded), hospital: false };
  }

  function isDeliveryPostalExcludedForDate(code, dateIso) {
    if (String(code || '').length !== 6 || !dateIso || POSTAL.isHospitalPostal(code)) return false;
    var datedPostal = POSTAL.validatePostalClient(
      code,
      config.excluded_areas || [],
      config.excluded_postal_prefixes || [],
      config.allowed_postal_exceptions || [],
      dateIso,
      config.scheduled_postal_exclusions,
    );
    return !!(datedPostal.valid && datedPostal.excluded);
  }

  function updateAfterPostalVisibility() {
    var addressGate = getEl('wf-ld-address-gate');
    var postalError = getEl('wf-ld-postal-error');
    var hospitalHint = getEl('wf-ld-postal-hospital-hint');
    var afterPostal = getEl('wf-ld-after-postal');
    if (!afterPostal) return;
    var methodDelivery = getEl('wf-ld-method-delivery');
    var isDelivery = methodDelivery && methodDelivery.checked;
    if (addressGate) addressGate.style.display = isDelivery ? 'block' : 'none';
    var postalValid = false;
    postalIsHospital = false;
    var code = '';
    if (isDelivery) {
      var postalInput = getDeliveryPostalInput();
      code = getDeliveryPostalCodeFromInput(postalInput);
      var postalState = resolveDeliveryPostalValid(code);
      postalValid = postalState.valid;
      postalIsHospital = postalState.hospital;
      var datedPostal = config && POSTAL.validatePostalClient(code, config.excluded_areas, config.excluded_postal_prefixes,
        config.allowed_postal_exceptions, getSelectedDateIso(), config.scheduled_postal_exclusions);
      var scheduledMessage = datedPostal && (datedPostal.scheduledExclusion || datedPostal.scheduledNotice) ? datedPostal.message : '';
      if (postalBlockedNotice && (postalBlockedNotice.code !== code || getSelectedDateIso())) postalBlockedNotice = null;
      if (postalBlockedNotice) scheduledMessage = postalBlockedNotice.message;
      if (postalError) {
        if (!postalDefaultErrorMessage) postalDefaultErrorMessage = postalError.textContent;
        postalError.setAttribute('role', 'status');
        postalError.setAttribute('aria-live', 'polite');
        postalError.textContent = scheduledMessage || postalDefaultErrorMessage;
        if (postalInput) postalInput.setAttribute('aria-describedby', 'wf-ld-postal-error');
        postalError.style.display = code.length === 6 && !postalIsHospital && (scheduledMessage || !postalValid) ? 'block' : 'none';
      }
      if (hospitalHint) hospitalHint.style.display = (code.length === 6 && postalIsHospital) ? 'block' : 'none';
    } else {
      postalValid = true;
      postalBlockedNotice = null;
      if (postalError) postalError.style.display = 'none';
      if (hospitalHint) hospitalHint.style.display = 'none';
    }
    var gateReady = !isDelivery || isDeliveryAddressGateReady(code, {
      streetFilled: isDeliveryStreetFilled(),
      postalValid: postalValid,
    });
    afterPostal.style.display = gateReady ? 'block' : 'none';
  }

  function validatePostalServer(code) {
    var requestId = ++postalValidationRequest;
    var deliveryMethodInput = getEl('wf-ld-method-delivery');
    var requestedDelivery = !!(deliveryMethodInput && deliveryMethodInput.checked);
    var requestedDate = getSelectedDateIso();
    postalServerValid = null;
    updateAfterPostalVisibility();
    if (POSTAL.isHospitalPostal(code)) {
      postalServerValid = true;
      updateAfterPostalVisibility();
      validateAndBlockCheckout();
      return;
    }
    API.fetchValidatePostal(code, requestedDelivery ? requestedDate : '').then(function (res) {
      if (requestedDelivery !== !!(deliveryMethodInput && deliveryMethodInput.checked)) return;
      if (requestId !== postalValidationRequest || code !== getDeliveryPostalCodeFromInput(getDeliveryPostalInput()) || requestedDate !== getSelectedDateIso()) return;
      if (!res) {
        updateAfterPostalVisibility();
        validateAndBlockCheckout();
        return;
      }
      if (requestedDelivery && res.scheduled_exclusion) {
        var blockedDate = getEl('wf-ld-date');
        if (datePicker && datePicker.setValue) datePicker.setValue('');
        else if (blockedDate) { blockedDate.value = ''; blockedDate.setAttribute('data-value', ''); }
        dateSelected = false;
        var standardSlot = getEl('wf-ld-timeslot');
        var expressSlot = getEl('wf-ld-express-timeslot');
        if (standardSlot) standardSlot.value = '';
        if (expressSlot) expressSlot.value = '';
        postalServerValid = true;
        postalBlockedNotice = { code: code, message: res.message || 'Delivery is not available to this area on that date. Please choose another delivery date or self-collection.' };
        updateAfterPostalVisibility();
        if (uiController) uiController.updateTimeslotLockState();
        validateAndBlockCheckout();
        return;
      }
      postalServerValid = res.valid && !res.excluded;
      updateAfterPostalVisibility();
      validateAndBlockCheckout();
    });
  }

  var availabilityData = null;
  function ensureTimeslotController() {
    if (timeslotController) return timeslotController;
    timeslotController = TIMESLOTS.createTimeslotController({
      SLOT: SLOT,
      isPastCutoff: DATE.isPastCutoff,
      fetchAvailability: API.fetchAvailability,
      getConfig: function () { return config; },
      getDateValue: function () {
        var dateEl = getEl('wf-ld-date');
        return dateEl && (dateEl.value || dateEl.getAttribute('data-value'));
      },
      getStandardSelect: function () { return getEl('wf-ld-timeslot'); },
      getExpressSelect: function () { return getEl('wf-ld-express-timeslot'); },
      getMethodCollectionChecked: function () {
        var methodCollection = getEl('wf-ld-method-collection');
        return !!(methodCollection && methodCollection.checked);
      },
      getAvailabilityData: function () { return availabilityData; },
      setAvailabilityData: function (next) { availabilityData = next; },
      getStandardSlotPricingMeta: getStandardSlotPricingMeta,
      getExpressSlotPricingMeta: getExpressSlotPricingMeta,
      formatExpressAmount: formatExpressAmount,
      updateExpressCardLabel: updateExpressCardLabel,
      updateStandardCardLabel: updateStandardCardLabel,
    });
    return timeslotController;
  }

  function deriveSlotLabel(s) {
    return TIMESLOTS.deriveSlotLabel(s);
  }

  function populateTimeslots(dateOverride) {
    ensureTimeslotController().populateTimeslots(dateOverride);
  }

  function fetchAvailabilityAndPopulate(dateVal) {
    return ensureTimeslotController().fetchAvailabilityAndPopulate(dateVal);
  }

  function ensureValidationController() {
    if (validationController) return validationController;
    validationController = VALIDATION.createValidationController({
      deps: {
        POSTAL: POSTAL,
        fetch: fetch,
      },
      dom: {
        getEl: getEl,
        getForm: function () { return (document.getElementById('cart') || document.getElementById('cart_form')); },
      },
      getters: {
        getConfig: function () { return config; },
        getSelectedDateIso: getSelectedDateIso,
        getActiveTimeslotValue: getActiveTimeslotValue,
        isDateAllowed: isDateAllowed,
        isSelectedTimeslotValid: isSelectedTimeslotValid,
        isActiveSlotPastCutoff: isActiveSlotPastCutoff,
        getFormVal: getFormVal,
        getActiveTimeslotType: function () { return activeTimeslotType; },
        getExpressSelectionError: function () { return expressSelectionError; },
        isBlackoutDate: isBlackoutDate,
        getSurchargeForDate: getSurchargeForDate,
        getSeasonalData: function () { return seasonalData; },
        getSeasonalDataPromise: function () { return seasonalDataPromise; },
        getSeasonalCheckPending: function () { return seasonalCheckPending; },
        getSeasonalConflictMessages: function () { return seasonalConflictMessages; },
      },
      setters: {
        setSeasonalCheckPending: function (next) { seasonalCheckPending = !!next; },
        setSeasonalConflictMessages: function (next) {
          seasonalConflictMessages = Array.isArray(next) ? next : [];
        },
      },
    });
    return validationController;
  }

  function ensureCheckoutFlowController() {
    if (checkoutFlowController) return checkoutFlowController;
    checkoutFlowController = CHECKOUT_FLOW.createCheckoutFlowController({
      deps: {
        API: API,
        POSTAL: POSTAL,
        EXPRESS: EXPRESS,
        STORAGE: STORAGE,
        CHECKOUT: CHECKOUT,
      },
      dom: {
        getEl: getEl,
      },
      getters: {
        getActiveTimeslotValue: getActiveTimeslotValue,
        getActiveTimeslotId: getActiveTimeslotId,
        getActiveTimeslotType: function () { return activeTimeslotType; },
        getExpressSelectionError: function () { return expressSelectionError; },
        getSelectedDateIso: getSelectedDateIso,
        getConfig: function () { return config; },
        isActiveSlotPastCutoff: isActiveSlotPastCutoff,
        getFormVal: getFormVal,
      },
      actions: {
        runSeasonalConflictCheck: runSeasonalConflictCheck,
        populateTimeslots: populateTimeslots,
        isDateAllowed: isDateAllowed,
        isSelectedTimeslotValid: isSelectedTimeslotValid,
        validateAndBlockCheckout: validateAndBlockCheckout,
        applyCollectionRecipientFallback: applyCollectionRecipientFallback,
        setDeliveryFeeRow: setDeliveryFeeRow,
        getSelectedExpressSlotMeta: getSelectedExpressSlotMeta,
        getSelectedStandardSlotMeta: getSelectedStandardSlotMeta,
      },
    });
    return checkoutFlowController;
  }

  function isBlackoutDate(dateStr) {
    if (!config || !config.blackout_dates || !dateStr) return false;
    return config.blackout_dates.some(function (b) { return b.date === dateStr; });
  }

  function getSurchargeForDate(dateStr) {
    if (!config || !config.surcharges || !dateStr) return null;
    for (var i = 0; i < config.surcharges.length; i++) {
      if (config.surcharges[i].date === dateStr) return config.surcharges[i];
    }
    return null;
  }

  function checkSeasonalConflicts(dateStr) {
    return ensureValidationController().checkSeasonalConflicts(dateStr);
  }

  function runSeasonalConflictCheck(dateStr, seasonalHint) {
    return ensureValidationController().runSeasonalConflictCheck(dateStr, seasonalHint);
  }

  function updateDateHints(dateStr) {
    return ensureValidationController().updateDateHints(dateStr);
  }

  function validateAndBlockCheckout(options) {
    return ensureValidationController().validateAndBlockCheckout(options);
  }

  // Emergency HF snapshot of /api/delivery/HF/public, refreshed 2026-10-02 (SGT).
  // Keep store-specific fee mappings and restrictions together; see docs/Cart-Form.md.
  // Expired dated rules are omitted. Live config/browser cache still take precedence.
  var FALLBACK_CONFIG = { booking_window: 150,
    timeslots:
     [ { id: 'sun-1', label: '11:00 to 15:00', startHour: 11, endHour: 15, cutoffHour: 8, cutoffMinute: 30, availableDays: [ 0 ] },
       { id: 'wk-1', label: '10:00 to 14:00', startHour: 10, endHour: 14, cutoffHour: 8, cutoffMinute: 0, availableDays: [ 1, 2, 3, 4, 5, 6 ] },
       { id: 'wk-2', label: '14:00 to 18:00', startHour: 14, endHour: 18, cutoffHour: 12, cutoffMinute: 30, availableDays: [ 1, 2, 3, 4, 5, 6 ] },
       { id: 'wk-3', label: '17:00 to 21:00', startHour: 17, endHour: 21, cutoffHour: 15, cutoffMinute: 30, availableDays: [ 1, 2, 3, 4, 5 ], gated: true } ],
    collection_timeslots:
     [ { id: 'pickup-mon-fri', label: '10:00 to 16:00', startHour: 10, endHour: 13, endMinute: 0, cutoffHour: 8, cutoffMinute: 0, availableDays: [ 1 ] },
       { id: 'c-1772419533978', label: '', startHour: 14, endHour: 16, startMinute: 0, cutoffHour: 14, cutoffMinute: 0, availableDays: [ 1 ] },
       { id: 'c-1772419605376-bhlihiy9ckb',
         label: '10:00 to 16:00',
         startHour: 11,
         endHour: 16,
         startMinute: 0,
         endMinute: 0,
         cutoffHour: 8,
         cutoffMinute: 30,
         availableDays: [ 0 ] },
       { id: 'c-1772423646059-sejnqx5hzt', label: '10:00 to 16:00', startHour: 10, endHour: 13, endMinute: 0, cutoffHour: 8, cutoffMinute: 0, availableDays: [ 2 ] },
       { id: 'c-1772423646059-2hwznvabh9h', label: '', startHour: 14, endHour: 16, startMinute: 0, cutoffHour: 14, cutoffMinute: 0, availableDays: [ 2 ] },
       { id: 'c-1772423648873-beqo3jtv97t', label: '10:00 to 16:00', startHour: 10, endHour: 13, endMinute: 0, cutoffHour: 8, cutoffMinute: 0, availableDays: [ 3 ] },
       { id: 'c-1772423648873-oz5yivoikcs', label: '', startHour: 14, endHour: 16, startMinute: 0, cutoffHour: 14, cutoffMinute: 0, availableDays: [ 3 ] },
       { id: 'c-1772423651117-uhx5i82eta9', label: '10:00 to 16:00', startHour: 10, endHour: 13, endMinute: 0, cutoffHour: 8, cutoffMinute: 0, availableDays: [ 4 ] },
       { id: 'c-1772423651117-oh07qwacy4d', label: '', startHour: 14, endHour: 16, startMinute: 0, cutoffHour: 14, cutoffMinute: 0, availableDays: [ 4 ] },
       { id: 'c-1772423653045-rqczh0oty5f', label: '10:00 to 16:00', startHour: 10, endHour: 13, endMinute: 0, cutoffHour: 8, cutoffMinute: 0, availableDays: [ 5 ] },
       { id: 'c-1772423653045-azf2f8wea1w', label: '', startHour: 14, endHour: 16, startMinute: 0, cutoffHour: 14, cutoffMinute: 0, availableDays: [ 5 ] },
       { id: 'c-1772423655194-bsojc6pow1h', label: '10:00 to 16:00', startHour: 10, endHour: 13, endMinute: 0, cutoffHour: 8, cutoffMinute: 0, availableDays: [ 6 ] },
       { id: 'c-1772423655194-w0xsojwpj8p', label: '', startHour: 14, endHour: 16, startMinute: 0, cutoffHour: 14, cutoffMinute: 0, availableDays: [ 6 ] } ],
    express_timeslots:
     [ { id: 'ex-1', label: '10:30 to 11:30', startHour: 10, endHour: 11, startMinute: 30, endMinute: 30, cutoffHour: 9, cutoffMinute: 30, availableDays: [ 1, 2, 3, 4, 5 ] },
       { id: 'ex-1773198127863-q2tnx45b3p',
         label: '10:30 to 11:30',
         startHour: 10,
         endHour: 11,
         startMinute: 30,
         endMinute: 30,
         cutoffHour: 9,
         cutoffMinute: 30,
         availableDays: [ 6 ] },
       { id: 'ex-2', label: '11:30 to 12:30', startHour: 11, endHour: 12, startMinute: 30, endMinute: 30, cutoffHour: 10, cutoffMinute: 30, availableDays: [ 1, 2, 3, 4, 5, 6 ] },
       { id: 'ex-3', label: '12:30 to 13:30', startHour: 12, endHour: 13, startMinute: 30, endMinute: 30, cutoffHour: 11, cutoffMinute: 30, availableDays: [ 1, 2, 3, 4, 5, 6 ] },
       { id: 'ex-4', label: '14:30 to 15:30', startHour: 14, endHour: 15, startMinute: 30, endMinute: 30, cutoffHour: 13, cutoffMinute: 30, availableDays: [ 1, 2, 3, 4, 5 ] },
       { id: 'ex-1773198235419-o181pvw6txi',
         label: '14:30 to 15:30',
         startHour: 14,
         endHour: 15,
         startMinute: 30,
         endMinute: 30,
         cutoffHour: 12,
         cutoffMinute: 30,
         availableDays: [ 6 ] },
       { id: 'ex-5', label: '15:30 to 16:30', startHour: 15, endHour: 16, startMinute: 30, endMinute: 30, cutoffHour: 14, cutoffMinute: 30, availableDays: [ 1, 2, 3, 4, 5 ] },
       { id: 'ex-1773198233348-iw2w8u6rlz',
         label: '15:30 to 16:30',
         startHour: 15,
         endHour: 16,
         startMinute: 30,
         endMinute: 30,
         cutoffHour: 12,
         cutoffMinute: 30,
         availableDays: [ 6 ] },
       { id: 'ex-6', label: '16:30 to 17:30', startHour: 16, endHour: 17, startMinute: 30, endMinute: 30, cutoffHour: 12, cutoffMinute: 30, availableDays: [ 6 ] },
       { id: 'ex-1773198222582-u7rx91ytot',
         label: '16:30 to 17:30',
         startHour: 16,
         endHour: 17,
         startMinute: 30,
         endMinute: 30,
         cutoffHour: 15,
         cutoffMinute: 30,
         availableDays: [ 5 ] },
       { id: 'ex-1773198211783-k94kjiw98me',
         label: '16:30 to 17:30',
         startHour: 16,
         endHour: 17,
         startMinute: 30,
         endMinute: 30,
         cutoffHour: 15,
         cutoffMinute: 30,
         availableDays: [ 4 ] },
       { id: 'ex-1773198199853-r4mk4agbe2e',
         label: '16:30 to 17:30',
         startHour: 16,
         endHour: 17,
         startMinute: 30,
         endMinute: 30,
         cutoffHour: 15,
         cutoffMinute: 30,
         availableDays: [ 3 ] },
       { id: 'ex-1773198188598-wovgw40ijg',
         label: '16:30 to 17:30',
         startHour: 16,
         endHour: 17,
         startMinute: 30,
         endMinute: 30,
         cutoffHour: 15,
         cutoffMinute: 30,
         availableDays: [ 2 ] },
       { id: 'ex-1773198165510-xp3rqj02v2',
         label: '16:30 to 17:30',
         startHour: 16,
         endHour: 17,
         startMinute: 30,
         endMinute: 30,
         cutoffHour: 15,
         cutoffMinute: 30,
         availableDays: [ 1 ] },
       { id: 'ex-1772419620543-uuye7b0jrs',
         label: '10:30 to 11:30',
         startHour: 10,
         endHour: 11,
         startMinute: 30,
         endMinute: 30,
         cutoffHour: 8,
         cutoffMinute: 30,
         availableDays: [ 0 ] },
       { id: 'ex-1772419620543-f311f2zlegk',
         label: '11:30 to 12:30',
         startHour: 11,
         endHour: 12,
         startMinute: 30,
         endMinute: 30,
         cutoffHour: 8,
         cutoffMinute: 30,
         availableDays: [ 0 ] },
       { id: 'ex-1772419620543-bbx2n3u6qir',
         label: '12:30 to 13:30',
         startHour: 12,
         endHour: 13,
         startMinute: 30,
         endMinute: 30,
         cutoffHour: 8,
         cutoffMinute: 30,
         availableDays: [ 0 ] },
       { id: 'ex-1772419620543-092y4ccnyheh',
         label: '14:30 to 15:30',
         startHour: 14,
         endHour: 15,
         startMinute: 30,
         endMinute: 30,
         cutoffHour: 8,
         cutoffMinute: 30,
         availableDays: [ 0 ] } ],
    delivery_pricing:
     { tiers:
        [ { id: 'tier_1779333223393', label: 'Night Delivery', amount: 10, variantId: '53676522701118', productId: 'gid://shopify/Product/12431799025982', active: true } ],
       slotTierMap: {} },
    express_product_variant_id: '',
    express_pricing:
     { tiers:
        [ { id: 'tier_2', label: 'Tier 2', amount: 25, variantId: '53676522602814', productId: 'gid://shopify/Product/12431798927678', active: true },
          { id: 'tier_3', label: 'Tier 4', amount: 35, variantId: '53676522635582', productId: 'gid://shopify/Product/12431798960446', active: true },
          { id: 'tier_1772632263455', label: 'Tier 3', amount: 30, variantId: '53676522668350', productId: 'gid://shopify/Product/12431798993214', active: true } ],
       slotTierMap:
        { 'ex-1': 'tier_2',
          'ex-2': 'tier_2',
          'ex-3': 'tier_2',
          'ex-4': 'tier_2',
          'ex-5': 'tier_2',
          'ex-6': 'tier_2',
          'ex-1772419620543-uuye7b0jrs': 'tier_1772632263455',
          'ex-1772419620543-f311f2zlegk': 'tier_2',
          'ex-1772419620543-bbx2n3u6qir': 'tier_2',
          'ex-1772419620543-092y4ccnyheh': 'tier_2',
          'ex-1773198127863-q2tnx45b3p': 'tier_2',
          'ex-1773198165510-xp3rqj02v2': 'tier_2',
          'ex-1773198188598-wovgw40ijg': 'tier_2',
          'ex-1773198199853-r4mk4agbe2e': 'tier_2',
          'ex-1773198211783-k94kjiw98me': 'tier_2',
          'ex-1773198222582-u7rx91ytot': 'tier_2',
          'ex-1773198233348-iw2w8u6rlz': 'tier_2',
          'ex-1773198235419-o181pvw6txi': 'tier_2' } },
    blackout_dates: [ { date: '2027-01-01', reason: 'New Year 2027' } ],
    excluded_areas:
     [ '088762',
       '129817',
       '498746',
       '498747',
       '498781',
       '498783',
       '498784',
       '498793',
       '498801',
       '507087',
       '508487',
       '518230',
       '534236',
       '539747',
       '567754',
       '596303',
       '637900',
       '688251',
       '688255',
       '688262',
       '698930',
       '708976',
       '708977',
       '718916',
       '718923',
       '729756',
       '757619',
       '757629',
       '757754',
       '757758',
       '758534',
       '759959',
       '778895' ],
    excluded_postal_prefixes: [ '63', '62', '81', '71', '7189' ],
    allowed_postal_exceptions: [],
    scheduled_postal_exclusions:
     [ { message: 'Your delivery address is unavailable during 7-12 Oct due to F1 Grand Prix.',
         label: 'F1 2026',
         prefixes: [ '038', '039', '178', '179', '189' ],
         valid_from: '2026-10-07',
         valid_to: '2026-10-12' } ],
    hospital_postal_codes:
     [ '169608',
       '119074',
       '308433',
       '529889',
       '768828',
       '159964',
       '228510',
       '258500',
       '574623',
       '188770',
       '229899',
       '307506',
       '329563',
       '238859',
       '258499',
       '248649',
       '308443',
       '119082',
       '168582',
       '168753',
       '168938',
       '169609',
       '169856',
       '329562',
       '427990',
       '529895',
       '544835',
       '544886',
       '569766',
       '609606',
       '768024' ],
    surcharges: [],
    override_days:
     [ { date: '2026-11-09',
         name: 'Deepavali PH Observed',
         delivery:
          { slots:
             [ { id: 'override-2026-11-09-d-0-1772630708016', label: '10:00 to 14:00', startHour: 10, endHour: 14, cutoffHour: 8, cutoffMinute: 0, availableDays: [ 1 ] },
               { id: 'override-2026-11-09-d-1-1772630708016', label: '14:00 to 18:00', startHour: 14, endHour: 18, cutoffHour: 12, cutoffMinute: 30, availableDays: [ 1 ] } ],
            slotLimits: {} },
         collection:
          { slots:
             [ { id: 'override-2026-11-09-c-0-1772630708016', label: '10:00 to 13:00', startHour: 10, endHour: 13, endMinute: 0, cutoffHour: 8, cutoffMinute: 0, availableDays: [ 1 ] },
               { id: 'override-2026-11-09-c-1-1772630708016',
                 label: '14:00 to 16:00',
                 startHour: 14,
                 endHour: 16,
                 startMinute: 0,
                 cutoffHour: 12,
                 cutoffMinute: 30,
                 availableDays: [ 1 ] } ],
            slotLimits: {} },
         express:
          { slots:
             [ { id: 'override-2026-11-09-ex-0-1772630708016',
                 label: '10:30 to 11:30',
                 startHour: 10,
                 endHour: 11,
                 startMinute: 30,
                 endMinute: 30,
                 cutoffHour: 9,
                 cutoffMinute: 30,
                 availableDays: [ 1 ] },
               { id: 'override-2026-11-09-ex-1-1772630708016',
                 label: '11:30 to 12:30',
                 startHour: 11,
                 endHour: 12,
                 startMinute: 30,
                 endMinute: 30,
                 cutoffHour: 10,
                 cutoffMinute: 30,
                 availableDays: [ 1 ] },
               { id: 'override-2026-11-09-ex-2-1772630708016',
                 label: '12:30 to 13:30',
                 startHour: 12,
                 endHour: 13,
                 startMinute: 30,
                 endMinute: 30,
                 cutoffHour: 11,
                 cutoffMinute: 30,
                 availableDays: [ 1 ] },
               { id: 'override-2026-11-09-ex-3-1772630708016',
                 label: '14:30 to 15:30',
                 startHour: 14,
                 endHour: 15,
                 startMinute: 30,
                 endMinute: 30,
                 cutoffHour: 12,
                 cutoffMinute: 30,
                 availableDays: [ 1 ] },
               { id: 'override-2026-11-09-ex-4-1772630708016',
                 label: '15:30 to 16:30',
                 startHour: 15,
                 endHour: 16,
                 startMinute: 30,
                 endMinute: 30,
                 cutoffHour: 12,
                 cutoffMinute: 30,
                 availableDays: [ 1 ] },
               { id: 'override-2026-11-09-ex-5-1772630708016',
                 label: '16:30 to 17:30',
                 startHour: 16,
                 endHour: 17,
                 startMinute: 30,
                 endMinute: 30,
                 cutoffHour: 12,
                 cutoffMinute: 30,
                 availableDays: [ 1 ] } ],
            slotLimits: {} } },
       { date: '2026-12-24',
         name: 'Christmas Eve',
         delivery:
          { slots:
             [ { id: 'override-2026-12-24-d-0-1772630838620', label: '10:00 to 14:00', startHour: 10, endHour: 14, cutoffHour: 8, cutoffMinute: 0, availableDays: [ 4 ] },
               { id: 'override-2026-12-24-d-1-1772630838620', label: '14:00 to 18:00', startHour: 14, endHour: 18, cutoffHour: 12, cutoffMinute: 30, availableDays: [ 4 ] } ],
            slotLimits: {} },
         collection:
          { slots:
             [ { id: 'override-2026-12-24-c-0-1772630838620', label: '10:00 to 13:00', startHour: 10, endHour: 13, endMinute: 0, cutoffHour: 8, cutoffMinute: 0, availableDays: [ 4 ] },
               { id: 'override-2026-12-24-c-1-1772630838620',
                 label: '14:00 to 16:00',
                 startHour: 14,
                 endHour: 16,
                 startMinute: 0,
                 cutoffHour: 12,
                 cutoffMinute: 30,
                 availableDays: [ 4 ] } ],
            slotLimits: {} },
         express:
          { slots:
             [ { id: 'override-2026-12-24-ex-0-1772630838620',
                 label: '10:30 to 11:30',
                 startHour: 10,
                 endHour: 11,
                 startMinute: 30,
                 endMinute: 30,
                 cutoffHour: 9,
                 cutoffMinute: 30,
                 availableDays: [ 4 ] },
               { id: 'override-2026-12-24-ex-1-1772630838620',
                 label: '11:30 to 12:30',
                 startHour: 11,
                 endHour: 12,
                 startMinute: 30,
                 endMinute: 30,
                 cutoffHour: 10,
                 cutoffMinute: 30,
                 availableDays: [ 4 ] },
               { id: 'override-2026-12-24-ex-2-1772630838620',
                 label: '12:30 to 13:30',
                 startHour: 12,
                 endHour: 13,
                 startMinute: 30,
                 endMinute: 30,
                 cutoffHour: 11,
                 cutoffMinute: 30,
                 availableDays: [ 4 ] },
               { id: 'override-2026-12-24-ex-3-1772630838620',
                 label: '14:30 to 15:30',
                 startHour: 14,
                 endHour: 15,
                 startMinute: 30,
                 endMinute: 30,
                 cutoffHour: 12,
                 cutoffMinute: 30,
                 availableDays: [ 4 ] },
               { id: 'override-2026-12-24-ex-4-1772630838620',
                 label: '15:30 to 16:30',
                 startHour: 15,
                 endHour: 16,
                 startMinute: 30,
                 endMinute: 30,
                 cutoffHour: 12,
                 cutoffMinute: 30,
                 availableDays: [ 4 ] },
               { id: 'override-2026-12-24-ex-5-1772630838620',
                 label: '16:30 to 17:30',
                 startHour: 16,
                 endHour: 17,
                 startMinute: 30,
                 endMinute: 30,
                 cutoffHour: 12,
                 cutoffMinute: 30,
                 availableDays: [ 4 ] } ],
            slotLimits: {} } },
       { date: '2026-12-25',
         name: 'Christmas Day PH',
         delivery:
          { slots:
             [ { id: 'override-2026-12-25-d-0-1772630796107', label: '10:00 to 14:00', startHour: 10, endHour: 14, cutoffHour: 8, cutoffMinute: 0, availableDays: [ 5 ] },
               { id: 'override-2026-12-25-d-1-1772630796107', label: '14:00 to 18:00', startHour: 14, endHour: 18, cutoffHour: 12, cutoffMinute: 30, availableDays: [ 5 ] } ],
            slotLimits: {} },
         collection:
          { slots:
             [ { id: 'override-2026-12-25-c-0-1772630796107', label: '10:00 to 13:00', startHour: 10, endHour: 13, endMinute: 0, cutoffHour: 8, cutoffMinute: 0, availableDays: [ 5 ] },
               { id: 'override-2026-12-25-c-1-1772630796107',
                 label: '14:00 to 16:00',
                 startHour: 14,
                 endHour: 16,
                 startMinute: 0,
                 cutoffHour: 12,
                 cutoffMinute: 30,
                 availableDays: [ 5 ] } ],
            slotLimits: {} },
         express:
          { slots:
             [ { id: 'override-2026-12-25-ex-0-1772630796107',
                 label: '10:30 to 11:30',
                 startHour: 10,
                 endHour: 11,
                 startMinute: 30,
                 endMinute: 30,
                 cutoffHour: 9,
                 cutoffMinute: 30,
                 availableDays: [ 5 ] },
               { id: 'override-2026-12-25-ex-1-1772630796107',
                 label: '11:30 to 12:30',
                 startHour: 11,
                 endHour: 12,
                 startMinute: 30,
                 endMinute: 30,
                 cutoffHour: 10,
                 cutoffMinute: 30,
                 availableDays: [ 5 ] },
               { id: 'override-2026-12-25-ex-2-1772630796107',
                 label: '12:30 to 13:30',
                 startHour: 12,
                 endHour: 13,
                 startMinute: 30,
                 endMinute: 30,
                 cutoffHour: 11,
                 cutoffMinute: 30,
                 availableDays: [ 5 ] },
               { id: 'override-2026-12-25-ex-3-1772630796107',
                 label: '14:30 to 15:30',
                 startHour: 14,
                 endHour: 15,
                 startMinute: 30,
                 endMinute: 30,
                 cutoffHour: 12,
                 cutoffMinute: 30,
                 availableDays: [ 5 ] },
               { id: 'override-2026-12-25-ex-4-1772630796107',
                 label: '15:30 to 16:30',
                 startHour: 15,
                 endHour: 16,
                 startMinute: 30,
                 endMinute: 30,
                 cutoffHour: 12,
                 cutoffMinute: 30,
                 availableDays: [ 5 ] },
               { id: 'override-2026-12-25-ex-5-1772630796107',
                 label: '16:30 to 17:30',
                 startHour: 16,
                 endHour: 17,
                 startMinute: 30,
                 endMinute: 30,
                 cutoffHour: 12,
                 cutoffMinute: 30,
                 availableDays: [ 5 ] } ],
            slotLimits: {} } },
       { date: '2026-12-31',
         name: 'NYE',
         delivery:
          { slots:
             [ { id: 'override-2026-12-31-d-0-1772630877619', label: '10:00 to 14:00', startHour: 10, endHour: 14, cutoffHour: 8, cutoffMinute: 0, availableDays: [ 4 ] },
               { id: 'override-2026-12-31-d-1-1772630877619', label: '14:00 to 18:00', startHour: 14, endHour: 18, cutoffHour: 12, cutoffMinute: 30, availableDays: [ 4 ] } ],
            slotLimits: {} },
         collection:
          { slots:
             [ { id: 'override-2026-12-31-c-0-1772630877619', label: '10:00 to 13:00', startHour: 10, endHour: 13, endMinute: 0, cutoffHour: 8, cutoffMinute: 0, availableDays: [ 4 ] },
               { id: 'override-2026-12-31-c-1-1772630877619',
                 label: '14:00 to 16:00',
                 startHour: 14,
                 endHour: 16,
                 startMinute: 0,
                 cutoffHour: 12,
                 cutoffMinute: 30,
                 availableDays: [ 4 ] } ],
            slotLimits: {} },
         express:
          { slots:
             [ { id: 'override-2026-12-31-ex-0-1772630877619',
                 label: '10:30 to 11:30',
                 startHour: 10,
                 endHour: 11,
                 startMinute: 30,
                 endMinute: 30,
                 cutoffHour: 9,
                 cutoffMinute: 30,
                 availableDays: [ 4 ] },
               { id: 'override-2026-12-31-ex-1-1772630877619',
                 label: '11:30 to 12:30',
                 startHour: 11,
                 endHour: 12,
                 startMinute: 30,
                 endMinute: 30,
                 cutoffHour: 10,
                 cutoffMinute: 30,
                 availableDays: [ 4 ] },
               { id: 'override-2026-12-31-ex-2-1772630877619',
                 label: '12:30 to 13:30',
                 startHour: 12,
                 endHour: 13,
                 startMinute: 30,
                 endMinute: 30,
                 cutoffHour: 11,
                 cutoffMinute: 30,
                 availableDays: [ 4 ] },
               { id: 'override-2026-12-31-ex-3-1772630877619',
                 label: '14:30 to 15:30',
                 startHour: 14,
                 endHour: 15,
                 startMinute: 30,
                 endMinute: 30,
                 cutoffHour: 12,
                 cutoffMinute: 30,
                 availableDays: [ 4 ] },
               { id: 'override-2026-12-31-ex-4-1772630877619',
                 label: '15:30 to 16:30',
                 startHour: 15,
                 endHour: 16,
                 startMinute: 30,
                 endMinute: 30,
                 cutoffHour: 12,
                 cutoffMinute: 30,
                 availableDays: [ 4 ] },
               { id: 'override-2026-12-31-ex-5-1772630877619',
                 label: '16:30 to 17:30',
                 startHour: 16,
                 endHour: 17,
                 startMinute: 30,
                 endMinute: 30,
                 cutoffHour: 12,
                 cutoffMinute: 30,
                 availableDays: [ 4 ] } ],
            slotLimits: {} } } ],
    excluded_products:
     [ '8154669285600',
       '8685199687934',
       '8685201522942',
       '8792853577952',
       '8881426301182',
       '8981967241440',
       '8982010200288',
       '8982014853344',
       '8982015082720',
       '8982015836384',
       '8982016262368',
       '8982017048800',
       '8982017310944',
       '8982925934816',
       '9092319248608',
       '9260463522016',
       '9260479709408',
       '12164077551934',
       '12168584167742',
       '4798862852158',
       '12222689411390',
       '11279832351038',
       '11091148341566',
       '10060603654462',
       '10060605030718',
       '11037863838014',
       '11038486266174',
       '11038615994686',
       '11038702207294',
       '11051840569662',
       '11092899037502',
       '11312012656958',
       '11312018489662',
       '11328696222014',
       '11328709656894',
       '11328711229758',
       '11328712737086',
       '11328713130302',
       '11328742293822',
       '11328772276542',
       '11328713949502',
       '11328741114174',
       '11328773357886',
       '11349367849278',
       '11358126178622',
       '11424497107262',
       '11575332241726',
       '11891786416446',
       '11736801476926',
       '11777760395582',
       '11894799761726',
       '11934437540158',
       '11940259823934',
       '11940259889470',
       '11940261658942',
       '11940261855550',
       '11991762829630',
       '12220228698430',
       '12220229255486',
       '12237080887614',
       '12238712406334',
       '12255943164222',
       '12368035021118',
       '12368035184958',
       '6974270046270' ],
    excluded_variants:
     [ '39672057102398',
       '47389903454526',
       '47389910303038',
       '49430111322430',
       '49430760096062',
       '49430893363518',
       '49430984589630',
       '49456731259198',
       '49548432834878',
       '50116134306110',
       '50116143743294',
       '50176277414206',
       '50176291078462',
       '50176294846782',
       '50176432603454',
       '50176636223806',
       '50176299663678',
       '50176426574142',
       '50176659292478',
       '50273208598846',
       '50463185043774',
       '50669892600126',
       '51644385067326',
       '51022021591358',
       '51175794409790',
       '51657835839806',
       '51843578069310',
       '51882285629758',
       '51882301358398',
       '51882303324478',
       '52803840672062',
       '52895739019582',
       '52909654442302',
       '52984258429246',
       '53414433653054',
       '53414434570558' ],
    occasion_collections: [],
    advance_booking_rules_resolved:
     [ { id: '27224a58-8512-4871-a416-1e714638fccf',
         name: '3 days in advance',
         leadDays: 3,
         methods: { delivery: true, express: true, collection: true },
         productIds: [ '10111996428606', '11096632918334' ],
         variantIds: [] },
       { id: 'd44dd744-3557-45d7-adf2-13d21e58d3a8',
         name: '2 days in advance',
         leadDays: 2,
         methods: { delivery: true, express: true, collection: true },
         productIds: [ '6900514324542', '12222689411390', '12569757876542', '12594147918142', '12603650048318' ],
         variantIds:
          [ '53862913540414',
            '53862979371326',
            '53984999440702',
            '53984999473470',
            '54127896953150',
            '54127896985918',
            '54127897018686',
            '54127897051454',
            '54127897084222',
            '54127897116990',
            '54356076167486',
            '54356076200254',
            '54356076233022',
            '54356076265790',
            '54356076298558',
            '54356076331326',
            '54423690969406',
            '54423691002174',
            '54423691034942',
            '54423691067710',
            '54423691100478',
            '54423691133246',
            '54423634215230',
            '54423634247998',
            '54423634280766',
            '54423691166014',
            '54423691198782',
            '54423691231550',
            '54423691264318',
            '54423691297086',
            '54423691329854',
            '54423691362622',
            '54423691395390',
            '54423691428158',
            '54443082055998',
            '54443082088766',
            '54443082121534' ] } ] };

  function detectOccasion(cart, occasions) {
    return DOMAIN.detectOccasion(cart, occasions);
  }

  function buildOccasionBlackouts(occasions) {
    return DOMAIN.buildOccasionBlackouts(occasions);
  }

  function getNonOccasionItems(cart, matchedOccasion) {
    return DOMAIN.getNonOccasionItems(cart, matchedOccasion);
  }

  function isMixedCart(cart, matchedOccasion) {
    return DOMAIN.isMixedCart(cart, matchedOccasion);
  }

  function currentAdvanceMethodKey() {
    var methodCollection = getEl('wf-ld-method-collection');
    return DOMAIN.currentAdvanceMethodKey(!!(methodCollection && methodCollection.checked), activeTimeslotType);
  }

  function intersectsAnyProduct(productIds, targetIds) {
    return DOMAIN.intersectsAnyProduct(productIds, targetIds);
  }

  function computeActiveAdvanceLeadDays() {
    return DOMAIN.computeActiveAdvanceLeadDays({
      matchedOccasion: matchedOccasion,
      methodKey: currentAdvanceMethodKey(),
      rules: advanceBookingRulesResolved,
      cartVariantIds: cartVariantIds,
      cartProductIds: cartProductIds,
    });
  }

  function escapeHtml(s) {
    if (!s) return '';
    var div = document.createElement('div');
    div.textContent = s;
    return div.innerHTML;
  }

  function showMixedCartBlock(cart, occ, wrap) {
    var nonOcc = getNonOccasionItems(cart, occ);
    var productNames = nonOcc.map(function (item) { return item.title || 'Item'; });
    var startFmt = DATE.formatDisplayDate(occ.start_date);
    var endFmt = DATE.formatDisplayDate(occ.end_date);
    var methodCollection = getEl('wf-ld-method-collection');
    var fulfilmentWord = methodCollection && methodCollection.checked ? 'collection' : 'delivery';
    var intro = 'Your cart contains both ' + occ.name + ' and regular day items.';
    var proceed = 'To proceed with ' + occ.name + ' ' + fulfilmentWord + ' (' + startFmt + ' to ' + endFmt + '), please remove the regular day items from your cart.';
    var alt = 'Or remove ' + occ.name + ' items to choose a different ' + fulfilmentWord + ' date.';
    var block = getEl('wf-ld-mixed-cart-block');
    if (!block) {
      block = document.createElement('div');
      block.id = 'wf-ld-mixed-cart-block';
      block.className = 'wf-ld-mixed-cart-block';
      var methodCards = wrap.querySelector('.wf-ld-method-cards');
      if (methodCards && methodCards.parentNode) {
        methodCards.parentNode.insertBefore(block, methodCards);
      } else {
        wrap.appendChild(block);
      }
    }
    var removeHtml = '';
    if (productNames.length > 0) {
      removeHtml = '<ul style="margin:8px 0 8px 18px;padding:0;">' +
        productNames.map(function (name) { return '<li>' + escapeHtml(name) + '</li>'; }).join('') +
        '</ul>';
    }
    block.innerHTML =
      '<p style="margin:0 0 8px;">' + escapeHtml(intro) + '</p>' +
      '<p style="margin:0 0 8px;">' + escapeHtml(proceed) + '</p>' +
      (removeHtml ? '<p style="margin:0 0 4px;">Remove:</p>' + removeHtml : '') +
      '<p style="margin:8px 0 8px;">' + escapeHtml(alt) + '</p>' +
      '<a href="/cart" style="font-weight:600;">Edit cart</a>';
    block.style.display = 'block';
  }

  function hideMixedCartBlock() {
    var block = getEl('wf-ld-mixed-cart-block');
    if (block) block.style.display = 'none';
  }

  function initFormDisabled(wrap) {
    wrap.classList.add('wf-ld-form-disabled');
    var form = (document.getElementById('cart') || document.getElementById('cart_form'));
    if (form) {
      var btn = form.querySelector('#checkout') || form.querySelector('button[type="submit"]');
      if (btn) btn.disabled = true;
    }
  }

  function clearFormDisabled(wrap) {
    wrap.classList.remove('wf-ld-form-disabled');
    var form = (document.getElementById('cart') || document.getElementById('cart_form'));
    if (form) {
      var btn = form.querySelector('#checkout') || form.querySelector('button[type="submit"]');
      if (btn) btn.disabled = false;
    }
  }

  function setBootstrapLoadingState(wrap, message) {
    if (!wrap) return;
    wrap.style.display = '';
    wrap.classList.add('wf-ld-pending');
    initFormDisabled(wrap);
    var hint = getEl('wf-ld-init-hint');
    if (hint) {
      hint.classList.remove('wf-ld-config-recovery');
      hint.style.display = 'block';
      if (message) hint.textContent = message;
    }
  }

  function clearBootstrapLoadingState(wrap) {
    if (!wrap) return;
    wrap.classList.remove('wf-ld-pending');
    wrap.style.display = '';
    var hint = getEl('wf-ld-init-hint');
    if (!hint) return;
    if (bootstrapRecoveryMessage) {
      hint.textContent = bootstrapRecoveryMessage;
      hint.classList.add('wf-ld-config-recovery');
      hint.style.display = 'block';
      return;
    }
    hint.classList.remove('wf-ld-config-recovery');
    hint.style.display = 'none';
  }

  function fetchCartSnapshot() {
    return API.fetchWithTimeout('/cart.js', { credentials: 'same-origin' }, 5000, function (r) { return r.json(); })
      .catch(function () { return null; });
  }

  function fetchPublicConfigWithDiagnostics() {
    if (!API || typeof API.fetchPublic !== 'function') return Promise.resolve(null);
    return API.fetchPublic();
  }

  function reportConfigLoadFailed(reason, err) {
    if (!API || typeof API.reportEvent !== 'function') return;
    var meta = { reason: reason || 'unknown' };
    if (API && typeof API.getLastPublicConfigResult === 'function') {
      var result = API.getLastPublicConfigResult();
      if (result) {
        meta.reason = result.reason || meta.reason;
        if (typeof result.statusCode !== 'undefined') meta.statusCode = result.statusCode;
        if (typeof result.elapsedMs !== 'undefined') meta.elapsedMs = result.elapsedMs;
        if (result.url) meta.configUrl = result.url;
        if (result.detail) meta.detail = result.detail;
        if (typeof result.attempts !== 'undefined') meta.attempts = result.attempts;
        if (result.diagnostics) meta.diagnostics = result.diagnostics;
        if (result.recoverySource) meta.recoverySource = result.recoverySource;
        if (typeof result.cacheAgeMs !== 'undefined') meta.cacheAgeMs = result.cacheAgeMs;
      }
    }
    if (err && !meta.detail) {
      meta.detail = err && err.message ? err.message : String(err);
    }
    API.reportEvent('config_load_failed', meta);
  }

  function submitCartFormToNativeCheckout() {
    var form = (document.getElementById('cart') || document.getElementById('cart_form'));
    if (!form) {
      window.location.href = '/checkout';
      return;
    }
    var checkoutInput = form.querySelector('input[name="checkout"]');
    if (!checkoutInput) {
      checkoutInput = document.createElement('input');
      checkoutInput.type = 'hidden';
      checkoutInput.name = 'checkout';
      checkoutInput.value = 'Checkout';
      form.appendChild(checkoutInput);
    }
    form.submit();
  }

  function clearBypassCheckoutHandler() {
    var btn = getEl('wf-ld-checkout');
    if (!btn || !btn.__wfLdBypassHandler) return;
    btn.removeEventListener('click', btn.__wfLdBypassHandler);
    btn.__wfLdBypassHandler = null;
    btn.removeAttribute('data-wf-ld-checkout-mode');
  }

  function attachBypassCheckoutHandler(reason) {
    var btn = getEl('wf-ld-checkout');
    if (!btn) return;
    clearBypassCheckoutHandler();
    var hint = getEl('wf-ld-checkout-hint');
    if (hint) hint.style.display = 'none';
    btn.disabled = false;
    btn.__wfLdBypassHandler = function (event) {
      event.preventDefault();
      btn.disabled = true;
      btn.textContent = 'Processing...';
      submitCartFormToNativeCheckout();
    };
    btn.addEventListener('click', btn.__wfLdBypassHandler);
    btn.setAttribute('data-wf-ld-checkout-mode', reason || 'native-bypass');
  }

  function buildExcludedIdSets(excludedProducts, excludedVariants) {
    var excSet = {};
    var excVariantSet = {};
    (excludedProducts || []).forEach(function (id) {
      var normalized = normalizeShopifyNumericId(id);
      if (normalized) excSet[normalized] = true;
    });
    (excludedVariants || []).forEach(function (id) {
      var normalized = normalizeShopifyNumericId(id);
      if (normalized) excVariantSet[normalized] = true;
    });
    return { productSet: excSet, variantSet: excVariantSet };
  }

  function isAutoBypassCartItem(item) {
    var productType = String((item && (item.product_type || item.productType)) || '').trim().toLowerCase();
    if (productType === 'mws_apo_generated') return true;
    var handle = String((item && item.handle) || '').trim().toLowerCase();
    if (handle.indexOf('mws_apo_') === 0) return true;
    return false;
  }

  function isExcludedByConfig(item, excludedSets) {
    if (!item || !excludedSets) return false;
    var productCandidates = [
      item.product_id,
      item.productId,
      item.product && item.product.id,
    ];
    var variantCandidates = [
      item.variant_id,
      item.id,
      item.variantId,
      item.variant && item.variant.id,
    ];
    var productExcluded = productCandidates.some(function (candidate) {
      var normalized = normalizeShopifyNumericId(candidate);
      return normalized && excludedSets.productSet[normalized];
    });
    var variantExcluded = variantCandidates.some(function (candidate) {
      var normalized = normalizeShopifyNumericId(candidate);
      return normalized && excludedSets.variantSet[normalized];
    });
    return productExcluded || variantExcluded;
  }

  function shouldBypassDeliveryForm(cart, excludedSets) {
    var items = (cart && cart.items) || [];
    if (items.length === 0) return false;
    var bypassCount = 0;
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      if (isAutoBypassCartItem(item) || isExcludedByConfig(item, excludedSets)) {
        bypassCount += 1;
      }
    }
    return bypassCount === items.length;
  }

  function debounce(fn, ms) {
    var t = null;
    return function () {
      if (t) clearTimeout(t);
      t = setTimeout(function () { t = null; fn(); }, ms);
    };
  }

  function disposeFormSession(wrap) {
    if (!wrap || typeof wrap.__wfLdV2Dispose !== 'function') return;
    try {
      wrap.__wfLdV2Dispose();
    } catch (err) {
      console.warn('[wf-ld-v2] dispose failed', err);
    }
    wrap.__wfLdV2Dispose = null;
  }

  function checkMixedCartAndReenable(wrap) {
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        cartVariantIds = uniqueCartVariantIds(cart);
        var occ = detectOccasion(cart, config.occasion_collections || []);
        if (!occ || !isMixedCart(cart, occ)) {
          if (mixedCartObserver) {
            mixedCartObserver.disconnect();
            mixedCartObserver = null;
          }
          if (mixedCartVisibilityHandler) {
            document.removeEventListener('visibilitychange', mixedCartVisibilityHandler);
            mixedCartVisibilityHandler = null;
          }
          hideMixedCartBlock();
          clearFormDisabled(wrap);
          matchedOccasion = occ;
          disposeFormSession(wrap);
          initForm(wrap);
          console.log('[wf-ld] Mixed cart cleared — form enabled');
        }
      })
      .catch(function () {});
  }

  function startMixedCartObservers(wrap) {
    if (mixedCartObserver || mixedCartVisibilityHandler) return;
    var debouncedCheck = debounce(function () { checkMixedCartAndReenable(wrap); }, 300);

    var target = document.querySelector('.cart__item-list') || document.querySelector('.cart__form');
    if (target) {
      mixedCartObserver = new MutationObserver(function () { debouncedCheck(); });
      mixedCartObserver.observe(target, { childList: true, subtree: true });
    }

    mixedCartVisibilityHandler = function () {
      if (document.visibilityState === 'visible') debouncedCheck();
    };
    document.addEventListener('visibilitychange', mixedCartVisibilityHandler);
  }

  function isCurrentBootstrap(bootstrapRunId, wrap) {
    return bootstrapRunId === bootstrapInitGeneration && document.getElementById('wf-ld-custom') === wrap;
  }

  function init() {
    var wrap = document.getElementById('wf-ld-custom');
    if (!wrap) return;
    bootstrapInitGeneration += 1;
    var bootstrapRunId = bootstrapInitGeneration;
    bootstrapRecoveryMessage = '';
    setBootstrapLoadingState(wrap, 'Loading delivery options...');

    function revealWrap() {
      clearBootstrapLoadingState(wrap);
    }

    Promise.all([fetchPublicConfigWithDiagnostics(), fetchCartSnapshot()]).then(function (results) {
      if (!isCurrentBootstrap(bootstrapRunId, wrap)) return;
      var c = results[0];
      var cart = results[1];
      config = c;
      if (!config) {
        config = FALLBACK_CONFIG;
        bootstrapRecoveryMessage = 'Live delivery settings are temporarily unavailable. You can continue, but please refresh before checkout if possible.';
        console.warn('[wf-ld] Using fallback config (API unavailable)');
        reportConfigLoadFailed('public_config_unavailable');
      } else if (API && typeof API.getLastPublicConfigResult === 'function') {
        var publicConfigResult = API.getLastPublicConfigResult();
        if (publicConfigResult && publicConfigResult.recovered) {
          bootstrapRecoveryMessage = 'We\u2019re using recently saved delivery settings while we reconnect. If your selected option changed, checkout may ask you to try again.';
          console.warn('[wf-ld] Using recently saved config (API unavailable)');
          reportConfigLoadFailed('public_config_recovered');
        }
      }
      try {
        var storeKey = API && typeof API.getStoreKey === 'function'
          ? API.getStoreKey()
          : ((window.WF_LD_RUNTIME && window.WF_LD_RUNTIME.storeKey) || 'WF');
        var tierVariantIds = collectExpressTierVariantIdsFromConfig(config);
        window.WF_LD_DEBUG_STATE = window.WF_LD_DEBUG_STATE || {};
        window.WF_LD_DEBUG_STATE.delivery = {
          orchestrator: (window.WF_LD_RUNTIME && window.WF_LD_RUNTIME.cartOrchestrator) || 'v1',
          storeKey: storeKey,
          expressVariantFallback: String(config.express_product_variant_id || ''),
          expressTierVariantIds: tierVariantIds,
          loadedAtIso: new Date().toISOString(),
        };
        console.info('[wf-ld][linkage] loaded public config', window.WF_LD_DEBUG_STATE.delivery);
      } catch (err) {}

      var excludedProducts = config.excluded_products || [];
      var excludedVariants = config.excluded_variants || [];
      if (window.WF_CART_LINE_MESSAGES && typeof window.WF_CART_LINE_MESSAGES.initMessageCards === 'function') {
        var messageCardConfig = config.message_card_config || {};
        if (messageCardConfig && typeof messageCardConfig === 'object') {
          var messageExcludedProducts = Array.isArray(messageCardConfig.excludedProductIds)
            ? messageCardConfig.excludedProductIds.map(String)
            : [];
          var mergedExcludedProducts = {};
          messageExcludedProducts.concat(excludedProducts || []).forEach(function (id) {
            var key = String(id || '').trim();
            if (key) mergedExcludedProducts[key] = true;
          });
          var digitalMediaConfig = messageCardConfig.digitalMedia && typeof messageCardConfig.digitalMedia === 'object'
            ? {
                enabled: messageCardConfig.digitalMedia.enabled === true,
                priceLabel: String(messageCardConfig.digitalMedia.priceLabel || '$7.90'),
                claimApiBase: String(messageCardConfig.digitalMedia.claimApiBase || ''),
                variantId: String(messageCardConfig.digitalMedia.variantId || messageCardConfig.digitalMedia.photoVariantId || ''),
              }
            : { enabled: false, priceLabel: '$7.90', claimApiBase: '', variantId: '' };
          var previewDigitalMediaConfig = window.WF_DIGITAL_CARD_PREVIEW_CONFIG;
          if (previewDigitalMediaConfig && previewDigitalMediaConfig.enabled === true) {
            digitalMediaConfig = {
              enabled: true,
              priceLabel: String(previewDigitalMediaConfig.priceLabel || '$7.90'),
              claimApiBase: String(previewDigitalMediaConfig.claimApiBase || ''),
              variantId: String(previewDigitalMediaConfig.variantId || previewDigitalMediaConfig.photoVariantId || ''),
            };
          }
          messageCardConfig = {
            enabled: messageCardConfig.enabled,
            propertyName: messageCardConfig.propertyName,
            excludedProductIds: Object.keys(mergedExcludedProducts),
            excludedVariantIds: Array.isArray(excludedVariants) ? excludedVariants.map(String) : [],
            digitalMedia: digitalMediaConfig,
          };
        }
        window.WF_CART_LINE_MESSAGES.initMessageCards(messageCardConfig);
      }
      var occasions = config.occasion_collections || [];
      advanceBookingRulesResolved = Array.isArray(config.advance_booking_rules_resolved)
        ? config.advance_booking_rules_resolved
        : [];
      if (!cart) {
        cartProductIds = [];
        cartVariantIds = [];
        clearBypassCheckoutHandler();
        revealWrap();
        initForm(wrap);
        return;
      }

      cartProductIds = uniqueCartProductIds(cart);
      cartVariantIds = uniqueCartVariantIds(cart);
      var excludedSets = buildExcludedIdSets(excludedProducts, excludedVariants);
      if (shouldBypassDeliveryForm(cart, excludedSets)) {
        clearBootstrapLoadingState(wrap);
        wrap.style.display = 'none';
        attachBypassCheckoutHandler('native-bypass-opt-out');
        console.log('[wf-ld] Delivery form bypassed: cart fully opt-out/APO-generated');
        return;
      }
      clearBypassCheckoutHandler();
      matchedOccasion = detectOccasion(cart, occasions);
      if (matchedOccasion && isMixedCart(cart, matchedOccasion)) {
        revealWrap();
        showMixedCartBlock(cart, matchedOccasion, wrap);
        initFormDisabled(wrap);
        startMixedCartObservers(wrap);
        console.log('[wf-ld] Mixed cart — occasion + regular day items; form blocked');
        return;
      }
      revealWrap();
      initForm(wrap);
    }).catch(function (err) {
      if (!isCurrentBootstrap(bootstrapRunId, wrap)) return;
      config = FALLBACK_CONFIG;
      bootstrapRecoveryMessage = 'Live delivery settings are temporarily unavailable. You can continue, but please refresh before checkout if possible.';
      console.warn('[wf-ld] Using fallback config (bootstrap failed)');
      reportConfigLoadFailed('bootstrap_exception', err);
      cartProductIds = [];
      cartVariantIds = [];
      clearBypassCheckoutHandler();
      revealWrap();
      initForm(wrap);
    });
  }

  function initForm(wrap) {
    clearBootstrapLoadingState(wrap);
    clearFormDisabled(wrap);
    clearBypassCheckoutHandler();
    disposeFormSession(wrap);
    // A silent cart refresh (qty stepper, message save, discount apply, config
    // reload) swaps the cart section's innerHTML, destroying and recreating the
    // #wf-ld-custom node. The disposeFormSession(wrap) call above therefore
    // no-ops — the fresh node carries no __wfLdV2Dispose — leaving the previous
    // UI controller alive but bound to the now-detached wrap. ensureUiController
    // would then return that stale controller and re-bind the Delivery|Express
    // toggle (and method cards) to dead, detached nodes, so clicks do nothing.
    // Dispose via the controller-scoped handle, which survives node replacement.
    if (typeof activeFormDispose === 'function') {
      try { activeFormDispose(); } catch (err) {}
      activeFormDispose = null;
    }
    formInitGeneration += 1;
    var currentGeneration = formInitGeneration;
    var disposers = [];

    function on(el, eventName, handler, options) {
      if (!el || !eventName || !handler) return;
      el.addEventListener(eventName, handler, options);
      disposers.push(function () {
        el.removeEventListener(eventName, handler, options);
      });
    }

    function syncFeeStateAfterTimeslotRefresh(summary) {
      var controller = ensureUiController();
      if (!controller || typeof controller.syncFeeStateAfterTimeslotRefresh !== 'function') {
        return Promise.resolve(false);
      }
      return controller.syncFeeStateAfterTimeslotRefresh(summary);
    }

    function disposeThisForm() {
      if (currentGeneration !== formInitGeneration) return;
      activeFormDispose = null;
      if (uiController && typeof uiController.dispose === 'function') {
        uiController.dispose();
        uiController = null;
      }
      if (datePicker && typeof datePicker.destroy === 'function') {
        datePicker.destroy();
      }
      datePicker = null;
      while (disposers.length > 0) {
        var dispose = disposers.pop();
        try {
          dispose();
        } catch (err) {}
      }
    }

    wrap.__wfLdV2Dispose = disposeThisForm;
    activeFormDispose = disposeThisForm;

    if (window.WF_LD_UPSELL && typeof window.WF_LD_UPSELL.init === 'function') {
      window.WF_LD_UPSELL.init();
    }

    var today = DATE.todayStrSgt();
    var defaultMax = DATE.maxDateStrSgt(config.booking_window || 120);
    var dateInput = getEl('wf-ld-date');

    activeAdvanceLeadDays = computeActiveAdvanceLeadDays();
    var pickerMin = DATE.addDaysStrSgt(activeAdvanceLeadDays || 0);
    var pickerMax = defaultMax;
    var mergedBlackouts = (config.blackout_dates || []).slice();
    var occasions = config.occasion_collections || [];

    function showRestoreWarning(message) {
      if (!message) return;
      var warn = getEl('wf-ld-restore-warning');
      if (!warn) {
        warn = document.createElement('div');
        warn.id = 'wf-ld-restore-warning';
        warn.style.cssText = 'background:#fff3f3;border:1px solid #ffc7c7;border-radius:6px;padding:10px 14px;margin-bottom:12px;font-size:13px;color:#8a1f1f;line-height:1.5;';
        var dateLabel = getEl('wf-ld-date-label');
        if (dateLabel && dateLabel.parentNode) {
          dateLabel.parentNode.insertBefore(warn, dateLabel);
        } else {
          wrap.insertBefore(warn, wrap.firstChild);
        }
      }
      warn.textContent = message;
      warn.style.display = 'block';
    }

    if (matchedOccasion) {
      pickerMin = matchedOccasion.start_date;
      pickerMax = matchedOccasion.end_date;
      console.log('[wf-ld] Occasion mode:', matchedOccasion.name, pickerMin, '→', pickerMax);
    } else if (occasions.length > 0) {
      var occBlackouts = buildOccasionBlackouts(occasions);
      mergedBlackouts = mergedBlackouts.concat(occBlackouts);
    }

    function updateOccasionBannerCopy() {
      if (!matchedOccasion) return;
      var banner = getEl('wf-ld-occasion-banner');
      if (!banner) return;
      var startFmt = DATE.formatDisplayDate(matchedOccasion.start_date);
      var endFmt = DATE.formatDisplayDate(matchedOccasion.end_date);
      var methodCollection = getEl('wf-ld-method-collection');
      var isCollection = methodCollection && methodCollection.checked;
      var modePhrase = isCollection ? 'collection available' : 'delivery available';
      banner.textContent =
        matchedOccasion.name + ' order — ' + modePhrase + ' ' + startFmt + ' to ' + endFmt + ' only';
    }

    // occasion banner
    var occasionBanner = getEl('wf-ld-occasion-banner');
    var advanceBookingBanner = getEl('wf-ld-advance-booking-banner');
    if (matchedOccasion) {
      if (!occasionBanner) {
        occasionBanner = document.createElement('div');
        occasionBanner.id = 'wf-ld-occasion-banner';
        occasionBanner.style.cssText = 'background:#fff8e1;border:1px solid #ffe082;border-radius:6px;padding:10px 14px;margin-bottom:14px;font-size:13px;color:#6d4c00;line-height:1.5;';
        var dateLabel = getEl('wf-ld-date-label');
        if (dateLabel && dateLabel.parentNode) {
          dateLabel.parentNode.insertBefore(occasionBanner, dateLabel);
        }
      }
      updateOccasionBannerCopy();
      occasionBanner.style.display = 'block';
    } else if (occasionBanner) {
      occasionBanner.style.display = 'none';
    }
    if (!advanceBookingBanner) {
      advanceBookingBanner = document.createElement('div');
      advanceBookingBanner.id = 'wf-ld-advance-booking-banner';
      advanceBookingBanner.style.cssText = 'background:#f3f8ff;border:1px solid #b8d7ff;border-radius:6px;padding:10px 14px;margin-bottom:14px;font-size:13px;color:#194a8d;line-height:1.5;display:none;';
      var dateLabelForAdvance = getEl('wf-ld-date-label');
      if (dateLabelForAdvance && dateLabelForAdvance.parentNode) {
        dateLabelForAdvance.parentNode.insertBefore(advanceBookingBanner, dateLabelForAdvance);
      }
    }

    function resetDateAndTimeslotSelection(showHintMessage) {
      if (datePicker && datePicker.setValue) {
        datePicker.setValue('');
      } else if (dateInput) {
        dateInput.value = '';
        dateInput.setAttribute('data-value', '');
      }
      dateSelected = false;
      var currentPostal = getDeliveryPostalCodeFromInput(getDeliveryPostalInput());
      if (currentPostal.length === 6) validatePostalServer(currentPostal);
      var std = getEl('wf-ld-timeslot');
      var exp = getEl('wf-ld-express-timeslot');
      if (std) std.value = '';
      if (exp) exp.value = '';
      updateDateHints('');
      populateTimeslots();
      updateTimeslotLockState();
      if (showHintMessage) {
        var checkoutHint = getEl('wf-ld-checkout-hint');
        if (checkoutHint) {
          checkoutHint.textContent = showHintMessage;
          checkoutHint.style.display = 'block';
        }
      }
    }

    function updateAdvanceBookingBanner() {
      if (!advanceBookingBanner) return;
      if (matchedOccasion || activeAdvanceLeadDays <= 0) {
        advanceBookingBanner.style.display = 'none';
        return;
      }
      var earliestIso = DATE.addDaysStrSgt(activeAdvanceLeadDays);
      var earliestFmt = DATE.formatDisplayDate(earliestIso);
      advanceBookingBanner.textContent =
        'Earliest available date is ' + earliestFmt + ' due to advance booking requirements.';
      advanceBookingBanner.style.display = 'block';
    }

    function isDateDisabledForPicker(iso) {
      if (!isDateAllowed(iso)) return true;
      var collEl = getEl('wf-ld-method-collection');
      var isColl = collEl && collEl.checked;
      var mode = isColl ? 'collection' : 'delivery';
      var stdSlots = isColl ? (config.collection_timeslots || []) : config.timeslots;
      var overrides = config.override_days || [];
      var stdAvail = SLOT.filterAvailableSlots(stdSlots, iso, config.blackout_dates, DATE.isPastCutoff, overrides, mode);
      if (stdAvail.length > 0) return false;
      if (!isColl && config.express_timeslots && config.express_timeslots.length > 0) {
        var expAvail = SLOT.filterAvailableSlots(
          config.express_timeslots,
          iso,
          config.blackout_dates,
          DATE.isPastCutoff,
          overrides,
          'express'
        );
        if (expAvail.length > 0) return false;
      }
      return true;
    }

    function formatCutoffClock(totalMinutes) {
      var h = Math.floor(totalMinutes / 60);
      var m = totalMinutes % 60;
      var suffix = h >= 12 ? 'PM' : 'AM';
      var h12 = h % 12;
      if (h12 === 0) h12 = 12;
      return h12 + (m ? ':' + String(m).padStart(2, '0') : '') + ' ' + suffix;
    }

    // Same-day availability nudge under the date field: shown only while today is
    // still orderable (advance-booking, blackout, occasion and cutoff rules all
    // respected via isDateDisabledForPicker + filterAvailableSlots).
    function updateSamedayHint() {
      var hintEl = getEl('wf-ld-sameday-hint');
      if (!hintEl) return;
      var hide = function () { hintEl.style.display = 'none'; };
      if (!config) return hide();
      var collEl = getEl('wf-ld-method-collection');
      var isColl = collEl && collEl.checked;
      var todayIso = DATE.todayStrSgt();
      if (isDateDisabledForPicker(todayIso)) return hide();
      var slots = isColl ? (config.collection_timeslots || []) : (config.timeslots || []);
      var avail = SLOT.filterAvailableSlots(
        slots,
        todayIso,
        config.blackout_dates,
        DATE.isPastCutoff,
        config.override_days || [],
        isColl ? 'collection' : 'delivery'
      );
      if (!avail.length) return hide();
      var latestCutoff = 0;
      avail.forEach(function (s) {
        var mins = (s.cutoffHour || 0) * 60 + (s.cutoffMinute || 0);
        if (mins > latestCutoff) latestCutoff = mins;
      });
      if (!latestCutoff) return hide();
      hintEl.textContent = (isColl
        ? 'Same-day collection available — order by '
        : 'Same-day delivery available — order by ') + formatCutoffClock(latestCutoff) + '.';
      hintEl.style.display = 'block';
    }

    function applyDynamicDateBounds() {
      var bounds = getDateBounds();
      if (datePicker && datePicker.updateOpts) {
        datePicker.updateOpts({ minDate: bounds.min, maxDate: bounds.max });
      } else if (dateInput) {
        dateInput.setAttribute('min', bounds.min);
        dateInput.setAttribute('max', bounds.max);
      }
      updateAdvanceBookingBanner();
      var selectedIso = getSelectedDateIso();
      if (selectedIso && !isDateAllowed(selectedIso)) {
        resetDateAndTimeslotSelection(
          'Selected date is no longer valid due to advance booking requirements. Please choose another date.',
        );
      }
    }

    if (dateInput && PICKER) {
      datePicker = PICKER.attach(dateInput, {
        minDate: pickerMin,
        maxDate: pickerMax,
        blackoutDates: mergedBlackouts,
        surcharges: config.surcharges || [],
        overrideDays: config.override_days || [],
        isDateDisabled: isDateDisabledForPicker,
        onSelect: function (iso) {
          var currentPostal = getDeliveryPostalCodeFromInput(getDeliveryPostalInput());
          var methodDelivery = getEl('wf-ld-method-delivery');
          if (methodDelivery && methodDelivery.checked && isDeliveryPostalExcludedForDate(currentPostal, iso)) {
            resetDateAndTimeslotSelection('Delivery is not available to this postal code on that date. Please choose another date.');
            return;
          }
          dateInput.setAttribute('data-value', iso);
          dateSelected = true;
          if (currentPostal.length === 6) validatePostalServer(currentPostal);
          updateDateHints(iso);
          Promise.resolve(fetchAvailabilityAndPopulate(iso)).then(function (summary) {
            return syncFeeStateAfterTimeslotRefresh(summary);
          });
          updateTimeslotLockState();
          onChange();
        },
      });

      if (matchedOccasion) {
        var startParts = matchedOccasion.start_date.split('-');
        datePicker.navigateToMonth(parseInt(startParts[0], 10), parseInt(startParts[1], 10) - 1);
      }
    } else if (dateInput) {
      dateInput.setAttribute('min', pickerMin);
      dateInput.setAttribute('max', pickerMax);
    }
    applyDynamicDateBounds();

    seasonalDataPromise = API.fetchSeasonal()
      .then(function (data) {
        seasonalData = (data && data.variantWindows) ? data.variantWindows : null;
      })
      .catch(function () {
        seasonalData = null;
      });
    seasonalDataPromise.then(function () {
      if (datePicker && typeof datePicker.updateOpts === 'function') {
        datePicker.updateOpts({ isDateDisabled: isDateDisabledForPicker });
      }
      var selectedDate = getSelectedDateIso();
      if (selectedDate && !isDateAllowedForSeasonalCart(selectedDate)) {
        resetDateAndTimeslotSelection(
          'Selected date is outside the allowed seasonal delivery window. Please choose another date.',
        );
        return;
      }
      if (selectedDate) updateDateHints(selectedDate);
    });

    var methodDelivery = getEl('wf-ld-method-delivery');
    var methodCollection = getEl('wf-ld-method-collection');
    function updateRecipientAddressSummary() {
      var summary = getEl('wf-ld-recipient-address-summary');
      if (!summary) return;
      var addr1 = String(getFormVal('recipient_address1') || '').trim();
      var addr2 = String(getFormVal('recipient_address2') || '').trim();
      var postal = getDeliveryPostalCodeFromInput(getDeliveryPostalInput());
      var parts = [];
      if (addr1) parts.push(addr1);
      if (addr2) parts.push(addr2);
      if (postal) parts.push(postal);
      summary.textContent = parts.join(', ');
      summary.style.display = parts.length ? 'block' : 'none';
    }

    function collectState() {
      var anonCb = getEl('wf-ld-anonymous');
      return {
        method: (methodDelivery && methodDelivery.checked) ? 'delivery' : 'collection',
        postal: getDeliveryPostalCodeFromInput(getDeliveryPostalInput()),
        rFirstName: getFormVal('recipient_first_name'),
        rLastName: getFormVal('recipient_last_name'),
        rPhone: getPhoneWithCode('wf-ld-recipient-phone', 'wf-ld-recipient-phone-code'),
        rAddr1: getFormVal('recipient_address1'),
        rAddr2: getFormVal('recipient_address2'),
        rPostal: getFormVal('recipient_postal'),
        sFirstName: getFormVal('sender_first_name'),
        sLastName: getFormVal('sender_last_name'),
        sEmail: getFormVal('sender_email'),
        sPhone: getPhoneWithCode('wf-ld-sender-phone', 'wf-ld-sender-phone-code'),
        anonymous: anonCb ? anonCb.checked : false,
        dateIso: getSelectedDateIso(),
        dateDisplay: (getEl('wf-ld-date') || {}).value || '',
        stdSlot: (getEl('wf-ld-timeslot') || {}).value || '',
        expSlot: (getEl('wf-ld-express-timeslot') || {}).value || '',
        timeslotType: activeTimeslotType,
        stateVersion: 3,
      };
    }

    function onChange() {
      updateAttributes();
      updateAfterPostalVisibility();
      updateRecipientAddressSummary();
      populateTimeslots();
      validateAndBlockCheckout();
      STORAGE.save(collectState());
      trackCartFormProgress();
    }

    function setFormVal(attrName, val) {
      if (val == null) return;
      var inp = getCartAttributeField(attrName);
      if (inp) inp.value = val;
    }

    function clearAddressConsistencyHint() {
      var hint = getEl('wf-ld-address-consistency-hint');
      if (!hint) return;
      hint.textContent = '';
      hint.style.display = 'none';
    }

    function showAddressConsistencyHint(message) {
      if (!message) return;
      var hint = getEl('wf-ld-address-consistency-hint');
      if (!hint) return;
      hint.textContent = message;
      hint.style.display = 'block';
    }

    function buildAddressConsistencyMismatchMessage(street, enteredPostal, suggestedPostal) {
      return (
        'Postal code ' +
        enteredPostal +
        ' does not match ' +
        street +
        '. Google Maps suggests ' +
        suggestedPostal +
        ' for this street. Update the street or pick a postal suggestion.'
      );
    }

    function runAddressConsistencyCheck(done) {
      done = typeof done === 'function' ? done : function () {};
      if (recipientAutocompleteApplying) {
        done(null);
        return;
      }
      if (!ADDRESS_AC || typeof ADDRESS_AC.verifyStreetPostalConsistency !== 'function') {
        done(null);
        return;
      }
      var street = getFormVal('recipient_address1');
      var postal = getFormVal('recipient_postal');
      if (!String(street || '').trim() || String(postal || '').trim().length !== 6) {
        clearAddressConsistencyHint();
        done(null);
        return;
      }
      ADDRESS_AC.verifyStreetPostalConsistency(street, postal, function (result) {
        if (!result || result.status === 'match' || result.status === 'skipped' || result.status === 'unavailable') {
          clearAddressConsistencyHint();
          done(null);
          return;
        }
        if (result.status === 'mismatch') {
          showAddressConsistencyHint(
            buildAddressConsistencyMismatchMessage(street, result.enteredPostal, result.suggestedPostal)
          );
          done(result);
          return;
        }
        done(null);
      });
    }

    function ensureAddressConsistencyBeforeCheckout(done) {
      done = typeof done === 'function' ? done : function () {};
      if (!methodDelivery || !methodDelivery.checked) {
        done(true);
        return;
      }
      var active = document.activeElement;
      if (
        active &&
        typeof active.blur === 'function' &&
        (active.name === 'attributes[recipient_address1]' || active.name === 'attributes[recipient_postal]')
      ) {
        active.blur();
      }
      runAddressConsistencyCheck(function (result) {
        if (result && result.status === 'mismatch') {
          validateAndBlockCheckout();
          done(false);
          return;
        }
        done(true);
      });
    }

    function scheduleAddressConsistencyCheck() {
      if (recipientAutocompleteApplying) return;
      if (addressConsistencyTimer) clearTimeout(addressConsistencyTimer);
      addressConsistencyTimer = setTimeout(function () {
        addressConsistencyTimer = null;
        runAddressConsistencyCheck();
      }, 400);
    }

    function showSavedAddressMismatchHint(enteredPostal, suggestedPostal) {
      showAddressConsistencyHint(
        'This saved address may be outdated. Google Maps suggests postal code ' +
        suggestedPostal +
        ' for this street (your saved address has ' +
        enteredPostal +
        '). Please double-check before checkout.'
      );
    }

    function verifySavedRecipientAddressPostal(savedAddress) {
      if (!ADDRESS_AC || typeof ADDRESS_AC.verifySavedAddressPostalMatch !== 'function') return;
      ADDRESS_AC.verifySavedAddressPostalMatch(savedAddress, function (result) {
        if (!result || result.status !== 'mismatch') return;
        showSavedAddressMismatchHint(result.enteredPostal, result.suggestedPostal);
      });
    }

    function applySavedRecipientAddress(savedAddress) {
      if (!savedAddress) return;
      clearAddressConsistencyHint();
      var postal = String(savedAddress.zip || '').trim();
      setFormVal('recipient_first_name', savedAddress.firstName || '');
      setFormVal('recipient_last_name', savedAddress.lastName || '');
      setPhoneWithCode('wf-ld-recipient-phone', 'wf-ld-recipient-phone-code', savedAddress.phone || '');
      setFormVal('recipient_address1', savedAddress.address1 || '');
      setFormVal('recipient_address2', savedAddress.address2 || '');
      setFormVal('recipient_postal', postal);
      syncPhoneHidden();
      if (postal.length === 6) {
        validatePostalServer(postal);
      }
      verifySavedRecipientAddressPostal(savedAddress);
      onChange();
    }

    function restoreState() {
      var s = STORAGE.load();
      if (!s) return;
      // Delivery is the storefront default. Do not let stale localStorage from a
      // prior cart/session make Self-collection the default on a fresh load.
      if (methodDelivery) {
        methodDelivery.checked = true;
      }
      if (methodCollection) methodCollection.checked = false;
      setFormVal('recipient_first_name', s.rFirstName);
      setFormVal('recipient_last_name', s.rLastName);
      setPhoneWithCode('wf-ld-recipient-phone', 'wf-ld-recipient-phone-code', s.rPhone);
      setFormVal('recipient_address1', s.rAddr1);
      setFormVal('recipient_address2', s.rAddr2);
      setFormVal('recipient_postal', s.rPostal || s.postal || '');
      setFormVal('sender_first_name', s.sFirstName);
      setFormVal('sender_last_name', s.sLastName);
      setFormVal('sender_email', s.sEmail);
      setPhoneWithCode('wf-ld-sender-phone', 'wf-ld-sender-phone-code', s.sPhone);
      var anonCb = getEl('wf-ld-anonymous');
      if (anonCb && s.anonymous) anonCb.checked = true;

      var restoredPostal = String(s.rPostal || s.postal || '').trim();
      if (restoredPostal.length === 6) validatePostalServer(restoredPostal);
      // Re-apply the previously selected delivery date + timeslot, RE-VALIDATED against current
      // availability. Silent cart refreshes (message save, qty change, discount apply, config
      // reload) re-render the panel empty; without this the date is lost and the date-gated
      // Delivery/Express toggle re-locks. A date that is now past cutoff / out of window is left
      // cleared with a notice (the slot re-select below also no-ops if the slot is gone).
      var savedDateIso = String(s.dateIso || '').trim();
      if (methodDelivery && methodDelivery.checked && isDeliveryPostalExcludedForDate(restoredPostal, savedDateIso)) {
        showRestoreWarning('Your previously selected delivery date is not available for this postal code. Please choose another date.');
        savedDateIso = '';
      }
      if (savedDateIso && isDateWithinBounds(savedDateIso) && isDateAllowed(savedDateIso) && isDateAllowedForSeasonalCart(savedDateIso)) {
        if (datePicker && datePicker.setValue) {
          datePicker.setValue(savedDateIso);
        } else {
          var savedDateInput = getEl('wf-ld-date');
          if (savedDateInput) {
            savedDateInput.value = s.dateDisplay || savedDateIso;
            savedDateInput.setAttribute('data-value', savedDateIso);
          }
        }
        dateSelected = true;
        updateDateHints(savedDateIso);
        Promise.resolve(fetchAvailabilityAndPopulate(savedDateIso)).then(function () {
          var ui = ensureUiController();
          if (s.timeslotType === 'express') ui.switchTimeslotType('express');
          var stdSel = getEl('wf-ld-timeslot');
          var expSel = getEl('wf-ld-express-timeslot');
          // Re-select the saved slot only if it is still an available option; dispatch 'change'
          // so the bound handler re-applies any express fee + re-validates. If the slot is gone
          // (past cutoff), the value won't stick and validateAndBlockCheckout flags it for re-pick.
          if (s.timeslotType === 'express' && s.expSlot && expSel) {
            expSel.value = s.expSlot;
            if (expSel.value === s.expSlot) expSel.dispatchEvent(new Event('change', { bubbles: true }));
          } else if (s.stdSlot && stdSel) {
            stdSel.value = s.stdSlot;
            if (stdSel.value === s.stdSlot) stdSel.dispatchEvent(new Event('change', { bubbles: true }));
          }
          ui.updateTimeslotLockState();
          validateAndBlockCheckout();
        });
      } else if (savedDateIso) {
        showRestoreWarning('Your previously selected delivery date is no longer available. Please reselect your delivery date and timeslot.');
      }

      STORAGE.save(collectState());
      trackCartFormProgress();

      return s;
    }

    var recipientBlock = getEl('wf-ld-recipient-block');
    var pickupAddressBlock = getEl('wf-ld-pickup-address');
    var dateLabelEl = getEl('wf-ld-date-label');

    function updateMethodDependentUI() {
      var isDelivery = methodDelivery && methodDelivery.checked;
      if (recipientBlock) recipientBlock.style.display = isDelivery ? 'block' : 'none';
      if (pickupAddressBlock) pickupAddressBlock.style.display = isDelivery ? 'none' : 'block';
      if (dateLabelEl) dateLabelEl.textContent = isDelivery ? 'Delivery date' : 'Collection date';
      if (datePicker && datePicker.updateOpts) {
        datePicker.updateOpts({
          displayPlaceholder: isDelivery ? 'Select delivery date' : 'Select collection date',
        });
      }
      updateOccasionBannerCopy();
      updateSamedayHint();
    }
    function updateRecipientVisibility() { updateMethodDependentUI(); }

    function ensureUiController() {
      if (uiController) return uiController;
      uiController = UI.createUiController({
        deps: {
          EXPRESS: EXPRESS,
          STORAGE: STORAGE,
        },
        dom: {
          getEl: getEl,
          wrap: wrap,
        },
        getters: {
          getConfig: function () { return config; },
          getMatchedOccasion: function () { return matchedOccasion; },
          getDatePicker: function () { return datePicker; },
          getDateSelected: function () { return dateSelected; },
          getActiveTimeslotType: function () { return activeTimeslotType; },
          getActiveAdvanceLeadDays: function () { return activeAdvanceLeadDays; },
          getExpressSelectionError: function () { return expressSelectionError; },
        },
        setters: {
          setDateSelected: function (next) { dateSelected = !!next; },
          setActiveTimeslotType: function (next) { activeTimeslotType = next; },
          setActiveAdvanceLeadDays: function (next) { activeAdvanceLeadDays = next || 0; },
          setExpressSelectionError: function (next) { expressSelectionError = next || ''; },
        },
        actions: {
          updateExpressCardLabel: updateExpressCardLabel,
          updateStandardCardLabel: updateStandardCardLabel,
          validateAndBlockCheckout: validateAndBlockCheckout,
          populateTimeslots: populateTimeslots,
          fetchAvailabilityAndPopulate: fetchAvailabilityAndPopulate,
          updateDateHints: updateDateHints,
          onChange: onChange,
          updateRecipientVisibility: updateRecipientVisibility,
          collectState: collectState,
          setDeliveryFeeRow: setDeliveryFeeRow,
          computeActiveAdvanceLeadDays: computeActiveAdvanceLeadDays,
          applyDynamicDateBounds: applyDynamicDateBounds,
          getSelectedExpressSlotMeta: getSelectedExpressSlotMeta,
          getSelectedStandardSlotMeta: getSelectedStandardSlotMeta,
          setPostalServerValid: function (next) { postalServerValid = next; },
          validatePostalServer: validatePostalServer,
          syncPhoneHidden: syncPhoneHidden,
          updateAttributes: updateAttributes,
          clearAddressConsistencyHint: clearAddressConsistencyHint,
          scheduleAddressConsistencyCheck: scheduleAddressConsistencyCheck,
          isRecipientAutocompleteApplying: function () { return recipientAutocompleteApplying; },
        },
      });
      return uiController;
    }

    function updateTimeslotTypeVisibility() {
      ensureUiController().updateTimeslotTypeVisibility();
    }

    function refreshAdvanceBookingState() {
      ensureUiController().refreshAdvanceBookingState();
    }

    function updateTimeslotLockState() {
      ensureUiController().updateTimeslotLockState();
    }

    function ensureSavedAddressController() {
      if (savedAddressController) return savedAddressController;
      if (!SAVED || typeof SAVED.createSavedAddressController !== 'function') return null;
      savedAddressController = SAVED.createSavedAddressController({
        document: document,
        actions: {
          applySavedAddress: applySavedRecipientAddress,
        },
      });
      return savedAddressController;
    }

    var cartUi = ensureUiController();
    cartUi.bindMethodCards();
    cartUi.bindTimeslotTypeCards();
    cartUi.bindFormInputs();
    on(document, 'wf:cart:message-saved', function () {
      cartUi.syncMethodCards();
      updateAfterPostalVisibility();
      updateRecipientVisibility();
      updateTimeslotTypeVisibility();
      updateTimeslotLockState();
      validateAndBlockCheckout();
    });

    var checkoutFlow = ensureCheckoutFlowController();
    bindCartFormKeyboardSubmitGuard((document.getElementById('cart') || document.getElementById('cart_form')), wrap);

    var ldCheckoutBtn = document.getElementById('wf-ld-checkout');
    if (ldCheckoutBtn) {
      var cartForm = (document.getElementById('cart') || document.getElementById('cart_form'));
      if (cartForm && cartForm.contains(ldCheckoutBtn)) {
        var checkoutWrap = ldCheckoutBtn.closest('.cart__price-breakdown') || ldCheckoutBtn.parentNode;
        cartForm.parentNode.insertBefore(checkoutWrap, cartForm.nextSibling);
      }

      on(ldCheckoutBtn, 'click', function () {
        if (!config) return;
        var validationState = validateAndBlockCheckout({ revealErrors: true, focusFirstInvalid: true });
        if (!validationState || !validationState.valid) {
          return;
        }
        ensureAddressConsistencyBeforeCheckout(function (ok) {
          if (!ok) {
            clearCheckoutTransition();
            return;
          }
          proceedLdCheckout();
        });
      });

      function clearCheckoutTransition() {
        var upsell = window.WF_LD_UPSELL;
        if (upsell && typeof upsell.hide === 'function') {
          upsell.hide();
        }
      }

      function proceedLdCheckout() {
        updateAttributes();
        var idleCheckoutLabel = ldCheckoutBtn.getAttribute('data-wf-ld-idle-label') || ldCheckoutBtn.textContent || 'Checkout';
        if (!ldCheckoutBtn.getAttribute('data-wf-ld-idle-label')) {
          ldCheckoutBtn.setAttribute('data-wf-ld-idle-label', idleCheckoutLabel);
        }
        ldCheckoutBtn.disabled = true;
        ldCheckoutBtn.textContent = 'Processing...';

        var MSG = window.WF_CART_LINE_MESSAGES;
        var msgPromise = (MSG && typeof MSG.prepareMessagesForCheckout === 'function')
          ? MSG.prepareMessagesForCheckout()
          : ((MSG && typeof MSG.saveAllUnsavedMessages === 'function')
              ? MSG.saveAllUnsavedMessages().then(function () { return { ok: true }; })
              : Promise.resolve({ ok: true }));

        msgPromise.then(function (messageState) {
          if (messageState && messageState.ok === false) {
            var messageHint = getEl('wf-ld-checkout-hint');
            if (messageHint && messageState.message) {
              messageHint.textContent = messageState.message;
              messageHint.style.display = 'block';
            }
            ldCheckoutBtn.disabled = false;
            ldCheckoutBtn.textContent = idleCheckoutLabel;
            if (typeof ldCheckoutBtn.removeAttribute === 'function') ldCheckoutBtn.removeAttribute('aria-busy');
            clearCheckoutTransition();
            return;
          }

          var UPSELL = window.WF_LD_UPSELL;
          console.log('[wf-ld] UPSELL exists?', !!UPSELL, 'isEnabled?', UPSELL && UPSELL.isEnabled());
          if (UPSELL && UPSELL.isEnabled() && typeof UPSELL.shouldShowForCart === 'function') {
            UPSELL.shouldShowForCart()
              .then(function (shouldShow) {
                if (shouldShow) {
                  console.log('[wf-ld] showing upsell modal');
                  UPSELL.show(function () { checkoutFlow.proceedToCheckout(ldCheckoutBtn); });
                  return;
                }
                console.log('[wf-ld] upsell tag gate skipped modal');
                checkoutFlow.proceedToCheckout(ldCheckoutBtn);
              })
              .catch(function (err) {
                console.warn('[wf-ld] upsell tag gate check failed, proceeding', err);
                checkoutFlow.proceedToCheckout(ldCheckoutBtn);
              });
          } else if (UPSELL && UPSELL.isEnabled()) {
            console.log('[wf-ld] showing upsell modal');
            UPSELL.show(function () { checkoutFlow.proceedToCheckout(ldCheckoutBtn); });
          } else {
            console.log('[wf-ld] skipping upsell, proceeding to checkout');
            checkoutFlow.proceedToCheckout(ldCheckoutBtn);
          }
        }).catch(function (err) {
          console.error('[wf-ld] message-card checkout preparation failed', err);
          var messageHint = getEl('wf-ld-checkout-hint');
          if (messageHint) {
            messageHint.textContent = 'We could not prepare your message card updates. Please try again.';
            messageHint.style.display = 'block';
          }
          ldCheckoutBtn.disabled = false;
          ldCheckoutBtn.textContent = idleCheckoutLabel;
          if (typeof ldCheckoutBtn.removeAttribute === 'function') ldCheckoutBtn.removeAttribute('aria-busy');
          clearCheckoutTransition();
        });
      }
    }

    restoreState();
    syncPhoneHidden();
    cartUi.syncMethodCards();
    updateAfterPostalVisibility();
    updateRecipientAddressSummary();
    updateRecipientVisibility();
    updateTimeslotTypeVisibility();
    refreshAdvanceBookingState();
    updateTimeslotLockState();
    updateExpressCardLabel();
    updateStandardCardLabel();
    ensureSavedAddressController();

    if (ADDRESS_AC && config.google_maps_key) {
      ADDRESS_AC.loadScript(config.google_maps_key);
      var acStreet = wrap.querySelector('[name="attributes[recipient_address1]"]');
      var acUnit = wrap.querySelector('[name="attributes[recipient_address2]"]');
      var acPostal = wrap.querySelector('[name="attributes[recipient_postal]"]');
      ADDRESS_AC.init(acStreet, acPostal, acUnit, {
        onPlaceApplyStart: function () {
          recipientAutocompleteApplying = true;
        },
        onPlaceApplyEnd: function (filledCode) {
          recipientAutocompleteApplying = false;
          clearAddressConsistencyHint();
          var code = String(filledCode || '').trim();
          if (code.length === 6) {
            validatePostalServer(code);
          }
          onChange();
        },
        onPostalPlaceApplyStart: function () {
          recipientAutocompleteApplying = true;
        },
        onPostalPlaceApplyEnd: function (filledCode) {
          recipientAutocompleteApplying = false;
          clearAddressConsistencyHint();
          var code = String(filledCode || '').trim();
          if (code.length === 6) {
            validatePostalServer(code);
          }
          updateRecipientAddressSummary();
          onChange();
        },
      });
    }

    if (EXPRESS.removeAllFeeProducts || EXPRESS.removeAllExpressProducts) {
      var resetPromise = EXPRESS.removeAllFeeProducts
        ? EXPRESS.removeAllFeeProducts()
        : EXPRESS.removeAllExpressProducts();
      resetPromise.then(function () {
        EXPRESS.refreshCartTotals();
        if (EXPRESS.setFeeChargeVisible) EXPRESS.setFeeChargeVisible(false);
        else if (EXPRESS.setExpressChargeVisible) EXPRESS.setExpressChargeVisible(false);
        setDeliveryFeeRow('Delivery Fee', null);
      });
    }

    validateAndBlockCheckout();
  }

    function disposeMixedCartObservers() {
      if (mixedCartObserver) {
        mixedCartObserver.disconnect();
        mixedCartObserver = null;
      }
      if (mixedCartVisibilityHandler) {
        document.removeEventListener('visibilitychange', mixedCartVisibilityHandler);
        mixedCartVisibilityHandler = null;
      }
    }

    return {
      init: init,
      startMixedCartObservers: startMixedCartObservers,
      disposeMixedCartObservers: disposeMixedCartObservers,
    };
  }

  window.WF_LD_CART_INIT = {
    createInitController: createInitController,
    _test: {
      isKeyboardSubmitProneField: isKeyboardSubmitProneField,
      getDeliveryPostalCodeFromInput: getDeliveryPostalCodeFromInput,
      resolveMappedPricingTierId: resolveMappedPricingTierId,
      isDeliveryAddressGateReady: isDeliveryAddressGateReady,
    },
  };
})();
